import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Player } from '@remotion/player';
import CounterTreyPlay from '../remotion-videos/CounterTreyPlay.jsx';
import WistiaVideo from './WistiaVideo.jsx';
import SalesChart from './charts/SalesChart.jsx';
import CustomersChart from './charts/CustomersChart.jsx';
import GirlAndCar from './GirlAndCar.jsx';
import CompanyBuilding from './CompanyBuilding.jsx';
import CarBreakdown from './CarBreakdown.jsx';
import SubscriptionTransitionDashboard from './SubscriptionTransitionDashboard.jsx';
import BatteryCard from './BatteryCard.jsx';
import BatteryIndicators from './BatteryIndicators.jsx';
import { Play, Pause, ArrowLeft, Trophy, MapPin, Award, Users, Zap, Battery, ChevronLeft, ChevronRight, Phone, Truck, Bell, Check, Camera, Home, Package, RefreshCw, User, AlertCircle, ArrowRight, ShieldOff } from 'lucide-react';

// Smart Battery Walkthrough Component
const SmartBatteryWalkthrough = () => {
  const [currentScreen, setCurrentScreen] = useState('welcome');
  const [batteryHealth, setBatteryHealth] = useState(100);
  const [showNotification, setShowNotification] = useState(false);
  const [animateProgress, setAnimateProgress] = useState(false);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const screenOrder = ['welcome', 'onboarding1', 'onboarding2', 'connect', 'home', 'delivery', 'orderConfirm', 'tracking'];
  const screenLabels = {
    welcome: "Welcome",
    onboarding1: "Onboarding (1/2)",
    onboarding2: "Onboarding (2/2)",
    connect: "Connect Battery",
    home: "Home Screen",
    delivery: "Order Delivery",
    orderConfirm: "Order Confirmation",
    tracking: "Track Order"
  };

  const handleInteraction = () => {
    setIsAutoPlaying(false);
  };

  const navigateToScreen = (screen) => {
    handleInteraction();
    setCurrentScreen(screen);
  };

  const navigate = (direction) => {
    handleInteraction(); // Disable autoplay when user manually navigates
    const currentIndex = screenOrder.indexOf(currentScreen);
    let nextIndex;
    if (direction === 'next') {
      nextIndex = (currentIndex + 1) % screenOrder.length;
    } else {
      nextIndex = (currentIndex - 1 + screenOrder.length) % screenOrder.length;
    }
    setCurrentScreen(screenOrder[nextIndex]);
  };

  const restartDemo = () => {
    handleInteraction();
    setCurrentScreen('welcome');
    setBatteryHealth(100);
    setShowNotification(false);
    setAnimateProgress(false);
  };

  useEffect(() => {
    if (currentScreen === 'home') {
      setAnimateProgress(true);
    }
  }, [currentScreen]);

  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setCurrentScreen(prevScreen => {
        const currentIndex = screenOrder.indexOf(prevScreen);
        const nextIndex = (currentIndex + 1) % screenOrder.length;
        const nextScreen = screenOrder[nextIndex];
        
        // Handle special cases after advancing
        if (nextScreen === 'home' && batteryHealth === 100) {
          setTimeout(() => setBatteryHealth(40), 1500); // Trigger low battery state
        }
        
        if (nextScreen === 'tracking') {
          setTimeout(() => {
            setShowNotification(true);
            setTimeout(() => setShowNotification(false), 4000);
          }, 1000);
        }
        
        return nextScreen;
      });
    }, 3000);

    return () => clearInterval(interval);
  }, [batteryHealth, isAutoPlaying]);

  function WelcomeScreen() {
    return (
      <div className="h-full bg-black flex flex-col items-center justify-between px-8 pt-20 pb-12 text-white">
        <div className="flex-1 flex flex-col items-center justify-center text-center">
          <div className="relative mb-16">
            <div className="absolute inset-0 animate-pulse">
              <div className="absolute inset-0 w-40 h-40 rounded-full border border-green-500/10"></div>
              <div className="absolute inset-4 w-32 h-32 rounded-full border border-green-500/20"></div>
              <div className="absolute inset-8 w-24 h-24 rounded-full border border-green-500/30"></div>
            </div>
            <div className="relative z-10"><InterstrateBattery /></div>
          </div>
          <h1 className="text-5xl font-bold tracking-wider mb-4">SMART BATTERY</h1>
          <p className="text-gray-500 text-sm tracking-widest">BY INTERSTATE</p>
        </div>
        <div className="w-full space-y-6">
          <div className="text-center">
            <h2 className="text-3xl font-semibold mb-3">Welcome</h2>
            <p className="text-gray-400">Monitor and track your battery in real time.</p>
          </div>
          <button onClick={() => navigateToScreen('onboarding1')} className="w-full bg-green-500 py-4 rounded-2xl font-semibold text-lg shadow-lg shadow-green-500/25">Get started</button>
          <p className="text-gray-500 text-center text-sm">Have an account? <span className="text-green-500 font-medium">Login now</span></p>
        </div>
      </div>
    );
  }

  function Onboarding1() {
    return (
      <div className="h-full bg-black flex flex-col items-center justify-between px-8 pt-20 pb-12">
        <div className="flex-1 flex flex-col items-center justify-center">
          <div className="bg-white rounded-3xl p-10 mb-10 shadow-2xl relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-green-50 rounded-full -translate-y-16 translate-x-16"></div>
            <div className="relative z-10">
              <div className="flex justify-end mb-6">
                <div className="flex items-center space-x-2">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <Zap className="w-5 h-5 text-green-600" />
                  </div>
                  <div className="w-8 h-8 bg-green-50 rounded-full flex items-center justify-center">
                    <RefreshCw className="w-4 h-4 text-green-600" />
                  </div>
                </div>
              </div>
              
              <div className="flex items-center justify-center space-x-6 mb-8">
                <InterstrateBattery />
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <div className="w-16 h-0.5 bg-gradient-to-r from-green-500 to-green-300"></div>
                  <Truck className="w-8 h-8 text-green-500" />
                </div>
              </div>
            </div>
          </div>
          <div className="text-center max-w-xs">
            <h2 className="text-2xl font-bold text-white mb-4">Replace Battery On-time</h2>
            <p className="text-gray-400 leading-relaxed">Replace your car battery like a pro with delivery or easy pickup options.</p>
          </div>
        </div>
        <div className="w-full">
          <button onClick={() => navigateToScreen('onboarding2')} className="w-full bg-green-500 text-white py-4 rounded-2xl font-semibold text-lg">Next</button>
        </div>
      </div>
    );
  }
  
  function Onboarding2() {
    return (
      <div className="h-full bg-black flex flex-col items-center justify-between px-8 pt-20 pb-12">
        <div className="flex-1 flex flex-col items-center justify-center">
          <div className="bg-white rounded-3xl p-10 mb-10 shadow-2xl relative overflow-hidden">
            <div className="absolute top-0 left-0 w-32 h-32 bg-green-50 rounded-full -translate-y-16 -translate-x-16"></div>
            <div className="relative z-10">
              <div className="flex justify-between items-start mb-8">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <Zap className="w-6 h-6 text-green-600" />
                </div>
                <Bell className="w-8 h-8 text-green-600 animate-pulse" />
              </div>
              
              <div className="flex flex-col items-center">
                <div className="w-24 h-24 bg-gradient-to-br from-green-100 to-green-50 rounded-full flex items-center justify-center mb-6 shadow-lg">
                  <span className="text-3xl font-bold text-green-600">100</span>
                </div>
                <InterstrateBattery />
              </div>
            </div>
          </div>
          <div className="text-center max-w-xs">
            <h2 className="text-2xl font-bold text-white mb-4">Battery Health Alerts</h2>
            <p className="text-gray-400 leading-relaxed">Receive real-time battery health alerts on your phone from your AI-powered battery.</p>
          </div>
        </div>
        <div className="w-full">
          <button onClick={() => navigateToScreen('connect')} className="w-full bg-green-500 text-white py-4 rounded-2xl font-semibold text-lg">Continue</button>
        </div>
      </div>
    );
  }

  function ConnectScreen() {
    const [isConnecting, setIsConnecting] = useState(true);
    
    useEffect(() => {
      setTimeout(() => setIsConnecting(false), 2000);
    }, []);
    
    return (
      <div className="h-full bg-gradient-to-b from-green-950 to-black flex flex-col">
        <StatusBar />
        <div className="flex-1 flex flex-col px-6 pt-16 pb-8">
          <div className="flex-1 flex flex-col items-center justify-center">
            <div className="w-full max-w-sm">
              {isConnecting ? (
                <div className="text-center mb-12">
                  <div className="relative inline-block mb-8">
                    <InterstrateBattery />
                    <div className="absolute -right-12 top-1/2 -translate-y-1/2">
                      <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" style={{animationDelay: '0.2s'}}></div>
                        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" style={{animationDelay: '0.4s'}}></div>
                      </div>
                    </div>
                  </div>
                  <div className="w-20 h-20 bg-gray-800 rounded-2xl flex items-center justify-center mx-auto animate-pulse">
                    <span className="text-white text-xs font-bold">NFC</span>
                  </div>
                  <p className="text-white mt-6 text-lg">Connecting...</p>
                </div>
              ) : (
                <>
                  <div className="text-center mb-8">
                    <div className="w-24 h-24 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-green-500/50">
                      <Check className="w-12 h-12 text-white" strokeWidth={3} />
                    </div>
                    <h2 className="text-3xl font-bold text-white">Connected</h2>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="bg-gray-900/50 backdrop-blur rounded-2xl p-4 border border-gray-800">
                      <p className="text-gray-500 text-sm mb-1">Vehicle</p>
                      <p className="text-white font-medium text-lg">Toyota Land Cruiser</p>
                    </div>
                    
                    <div className="bg-gray-900/50 backdrop-blur rounded-2xl p-4 border border-gray-800">
                      <p className="text-gray-500 text-sm mb-2">VIN</p>
                      <div className="flex items-center justify-between">
                        <p className="text-white font-mono text-sm">UUHKHSDHDS1S21321S</p>
                        <button className="w-10 h-10 bg-gray-800 rounded-xl flex items-center justify-center">
                          <Camera className="w-5 h-5 text-gray-400" />
                        </button>
                      </div>
                    </div>
                    
                    <p className="text-gray-600 text-center text-sm mt-4">How to add a VIN number?</p>
                  </div>
                </>
              )}
            </div>
          </div>
          
          {!isConnecting && (
            <button
              onClick={() => {
                navigateToScreen('home');
                setTimeout(() => {
                  setBatteryHealth(40);
                  setShowNotification(true);
                  setTimeout(() => setShowNotification(false), 4000);
                }, 3000);
              }}
              className="w-full bg-green-500 text-white py-4 rounded-2xl font-semibold text-lg shadow-lg shadow-green-500/25 hover:bg-green-600 transition-all"
            >
              Continue
            </button>
          )}
        </div>
      </div>
    );
  }

  function DeliveryScreen() {
    return (
      <div className="h-full bg-gradient-to-b from-green-950 to-black flex flex-col">
        <StatusBar />
        <div className="flex items-center px-6 pt-16 pb-4">
          <button 
            onClick={() => navigateToScreen('home')}
            className="w-10 h-10 rounded-full bg-gray-900/50 flex items-center justify-center mr-4"
          >
            <ChevronLeft className="w-5 h-5 text-white" />
          </button>
          <h1 className="text-white text-xl font-semibold">Add delivery details</h1>
        </div>
        
        <div className="flex-1 px-6 pb-8 overflow-y-auto">
          <div className="space-y-4">
            <div>
              <p className="text-gray-400 text-sm mb-2 ml-1">Order type</p>
              <div className="bg-gray-900/50 backdrop-blur rounded-2xl p-4 border border-gray-800 flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-green-500/20 rounded-xl flex items-center justify-center mr-3">
                    <Truck className="w-5 h-5 text-green-400" />
                  </div>
                  <span className="text-white font-medium">Delivery</span>
                </div>
                <button className="text-gray-500 text-sm">Change</button>
              </div>
            </div>
            
            <div>
              <input
                type="text"
                placeholder="Enter delivery address"
                className="w-full bg-white rounded-2xl px-6 py-4 text-gray-900 placeholder-gray-400 font-medium"
                defaultValue="10320 Camino Santa Fe, San Diego CA"
              />
              <button className="flex items-center text-gray-400 text-sm mt-3 ml-1">
                <div className="w-5 h-5 rounded-full bg-gray-800 flex items-center justify-center mr-2">
                  <MapPin className="w-3 h-3 text-gray-400" />
                </div>
                Use my current location
              </button>
            </div>
            
            <div className="bg-gray-900/50 backdrop-blur rounded-2xl p-1 h-52 border border-gray-800 overflow-hidden">
              <div className="w-full h-full bg-gradient-to-br from-green-900/20 to-gray-900/50 rounded-xl flex items-center justify-center">
                <div className="text-center">
                  <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
                    <MapPin className="w-8 h-8 text-green-400" />
                  </div>
                  <p className="text-gray-400 text-sm">Tap to set pin location on map</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center text-green-400 text-sm bg-green-500/10 rounded-xl px-4 py-3">
              <Check className="w-5 h-5 mr-2 flex-shrink-0" />
              <span>9345 Cabot Drive, San Diego, CA</span>
            </div>
            
            <div>
              <input
                type="tel"
                placeholder="Phone number"
                className="w-full bg-white rounded-2xl px-6 py-4 text-gray-900 placeholder-gray-400 font-medium"
                defaultValue="865-405-3455"
              />
            </div>
          </div>
        </div>
        
        <div className="px-6 pb-8">
          <button
            onClick={() => navigateToScreen('orderConfirm')}
            className="w-full bg-green-500 text-white py-4 rounded-2xl font-semibold text-lg shadow-lg shadow-green-500/25 hover:bg-green-600 transition-all"
          >
            Submit order
          </button>
        </div>
      </div>
    );
  }

  function OrderConfirmScreen() {
    useEffect(() => {
      setTimeout(() => navigateToScreen('tracking'), 2000);
    }, []);
    
    return (
      <div className="h-full bg-gradient-to-b from-green-950 to-black flex flex-col items-center justify-center px-8">
        <StatusBar />
        <div className="bg-gray-900/50 backdrop-blur rounded-3xl p-8 text-center">
          <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-green-500/50">
            <Check className="w-10 h-10 text-white" strokeWidth={3} />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Order Confirmed</h2>
          <p className="text-gray-400">Order #72145</p>
          
          <div className="mt-8 space-y-4">
            <div className="flex items-center justify-center space-x-4 text-left">
              <Truck className="w-6 h-6 text-green-400" />
              <div>
                <p className="text-white font-medium">Delivery</p>
                <p className="text-gray-500 text-sm">9345 Cabot Drive, San Diego, CA</p>
              </div>
            </div>
            
            <div className="flex justify-center">
              <div className="flex space-x-1">
                <div className="w-8 h-1 bg-green-500 rounded animate-pulse"></div>
                <div className="w-8 h-1 bg-green-500 rounded animate-pulse" style={{animationDelay: '0.2s'}}></div>
                <div className="w-8 h-1 bg-green-500 rounded animate-pulse" style={{animationDelay: '0.4s'}}></div>
              </div>
            </div>
            
            <p className="text-gray-400 text-sm">Your battery is being packed and will be delivered to you soon.</p>
            <p className="text-gray-500 text-xs">(Order confirmed for flow)</p>
          </div>
        </div>
      </div>
    );
  }

  function TrackingScreen() {
    return (
      <div className="h-full bg-gradient-to-b from-green-950 to-black flex flex-col">
        <StatusBar />
        <div className="px-6 pt-16 pb-2">
          <div className="text-center">
            <div className="flex items-center justify-center mb-1">
              <Truck className="w-4 h-4 text-white mr-2" />
              <h1 className="text-white text-base font-medium">Order # 22521</h1>
            </div>
            <p className="text-gray-500 text-xs">December 04, 2024 6:00 PM (GMT-4)</p>
          </div>
        </div>
        
        <div className="flex-1 px-6 pb-4 overflow-y-auto space-y-3">
          <div className="bg-gray-900/50 backdrop-blur rounded-xl border border-gray-800 overflow-hidden">
            <div className="h-24 bg-gradient-to-br from-green-900/30 to-gray-900/50 relative">
              <div className="absolute bottom-3 left-4 right-4">
                <div className="flex items-center justify-between">
                  <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                    <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                  </div>
                  <div className="flex-1 mx-2 border-t border-dashed border-green-400/50"></div>
                  <div className="w-6 h-6 bg-green-500/30 rounded-full flex items-center justify-center">
                    <MapPin className="w-3 h-3 text-green-400" />
                  </div>
                </div>
              </div>
            </div>
            
            <div className="p-4">
              <div className="mb-3">
                <p className="text-gray-400 text-xs mb-2">Drop-off location</p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <MapPin className="w-4 h-4 text-green-400 mr-2" />
                    <p className="text-white text-sm">9345 Cabot Drive, San Diego, CA</p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-white">1.3</p>
                    <p className="text-gray-500 text-xs">mi</p>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-center space-x-1 mb-3">
                <div className="w-16 h-0.5 bg-green-500 rounded"></div>
                <div className="w-16 h-0.5 bg-gray-700 rounded"></div>
                <div className="w-16 h-0.5 bg-gray-700 rounded"></div>
              </div>
              
              <p className="text-gray-400 text-center text-xs mb-3">
                The driver has picked up your battery.
              </p>
            </div>
          </div>

          <div className="bg-gray-900/50 backdrop-blur rounded-xl border border-gray-800 p-4">
            <p className="text-gray-400 text-xs mb-3">Driver details</p>
            <div className="flex items-center mb-2">
              <div className="w-8 h-8 bg-gray-700 rounded-full mr-3"></div>
              <span className="text-white text-sm font-medium">Mark Allen</span>
            </div>
            <div className="flex items-center">
              <Phone className="w-4 h-4 text-gray-400 mr-3" />
              <span className="text-white text-sm">(858) 271 5003</span>
            </div>
          </div>
          
          <div className="bg-gray-900/50 backdrop-blur rounded-xl p-4 border border-gray-800">
            <p className="text-gray-400 text-xs mb-3">Order details</p>
            <div className="flex items-center">
              <InterstrateBattery size="small" />
              <div className="flex-1 ml-3">
                <p className="text-white font-semibold text-sm mb-1">MTX-94R/H7</p>
                <p className="text-2xl font-bold text-white">$154</p>
              </div>
              <div className="bg-gray-800 rounded-lg px-3 py-2">
                <p className="text-gray-500 text-xs">Qty</p>
                <p className="text-white font-bold">1</p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="px-6 pb-6">
          <button 
            onClick={() => {
              setBatteryHealth(100);
              navigateToScreen('home');
            }}
            className="w-full bg-green-500 text-white py-3 rounded-xl font-semibold shadow-lg shadow-green-500/25 hover:bg-green-600 transition-all"
          >
            Cancel order
          </button>
        </div>
      </div>
    );
  }

  useEffect(() => {
    const interval = setInterval(() => {
      if (currentScreen === 'home' && batteryHealth <= 40) {
        setShowNotification(true);
        setTimeout(() => setShowNotification(false), 3000);
      }
    }, 8000);
    return () => clearInterval(interval);
  }, [currentScreen, batteryHealth]);

  const screens = {
    welcome: <WelcomeScreen />,
    onboarding1: <Onboarding1 />,
    onboarding2: <Onboarding2 />,
    connect: <ConnectScreen />,
    home: <HomeScreen />,
    delivery: <DeliveryScreen />,
    orderConfirm: <OrderConfirmScreen />,
    tracking: <TrackingScreen />,
  };

  function StatusBar() {
    return (
      <div className="absolute top-0 left-0 right-0 h-11 flex items-center justify-between px-8 text-white text-sm font-medium z-10">
        <span>9:41</span>
        <div className="flex items-center space-x-1">
          <div className="w-4 h-4">
            <svg viewBox="0 0 24 24" fill="white">
              <path d="M2 17h20v2H2zm1.15-4.05L4 11.47l.85 1.48 1.3-.75-.85-1.48H7v-1.5H5.3l.85-1.48L4.85 7 4 8.47 3.15 7l-1.3.75.85 1.48H1v1.5h1.7l-.85 1.48 1.3.75zm6.7-.75l1.48.85 1.48-.85-.85-1.48H14v-1.5h-2.05l.85-1.48-1.48-.85L10 8.47 8.68 7l-1.48.85.85 1.48H6v1.5h2.05l-.85 1.48zm8 0l1.48.85 1.48-.85-.85-1.48H22v-1.5h-2.05l.85-1.48-1.48-.85L18 8.47 16.68 7l-1.48.85.85 1.48H14v1.5h2.05l-.85 1.48z"/>
            </svg>
          </div>
          <div className="w-4 h-4">
            <svg viewBox="0 0 24 24" fill="white">
              <path d="M1 9l2-2v8a2 2 0 002 2h14a2 2 0 002-2V7l2 2V9l-2-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v2L1 9z"/>
            </svg>
          </div>
          <div className="w-6 h-3">
            <svg viewBox="0 0 24 12" fill="white">
              <rect x="0" y="2" width="20" height="8" rx="2" fill="none" stroke="white" strokeWidth="1"/>
              <rect x="21" y="4.5" width="2" height="3" rx="0.5"/>
              <rect x="2" y="4" width="16" height="4" rx="1"/>
            </svg>
          </div>
        </div>
      </div>
    );
  }

  function InterstrateBattery({ size = 'medium' }) {
    const sizes = {
      small: 'w-20 h-16',
      medium: 'w-24 h-20',
      large: 'w-32 h-24'
    };
    
    return (
      <div className={`${sizes[size]} bg-gradient-to-b from-green-600 to-green-700 rounded-xl shadow-xl relative overflow-hidden`}>
        <div className="absolute inset-0 bg-gradient-to-br from-transparent to-black/20"></div>
        <div className="absolute top-2 left-2 right-2">
          <div className="flex justify-between">
            <div className="w-3 h-4 bg-gray-800 rounded-sm"></div>
            <div className="w-3 h-4 bg-red-600 rounded-sm"></div>
          </div>
        </div>
        <div className="absolute bottom-3 left-0 right-0 text-center">
          <p className="text-white font-bold text-xs tracking-wider">INTERSTATE</p>
        </div>
      </div>
    );
  }

  function HomeScreen() {
    const isHealthy = batteryHealth > 50;
    
    return (
      <div className="h-full bg-gradient-to-b from-green-950 to-black flex flex-col">
        <StatusBar />
        
        {/* Push Notification */}
        {showNotification && (
          <div className="absolute top-14 left-4 right-4 z-50 animate-slide-down">
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
              <div className="p-4">
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-green-500 rounded-xl flex items-center justify-center mr-3">
                    <Package className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold text-gray-900">Smart Battery</h3>
                        <p className="text-gray-600 text-sm mt-0.5">Driver is arriving soon.</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-gray-900">1 min</p>
                        <p className="text-xs text-gray-500">estimated time</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-3 px-12">
                  <div className="relative h-1 bg-gray-200 rounded-full overflow-hidden">
                    <div className="absolute left-0 top-0 h-full bg-green-500 animate-progress" style={{width: '60%'}}></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        
        <div className="flex-1 px-6 pt-16 pb-20">
          <div className={`bg-gradient-to-b ${isHealthy ? 'from-gray-900/50' : 'from-gray-900/70'} to-transparent rounded-3xl p-6 backdrop-blur`}>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <div className={`w-14 h-14 ${isHealthy ? 'bg-green-500' : 'bg-red-500'} rounded-2xl flex items-center justify-center shadow-lg`}>
                  {isHealthy ? <Check className="w-7 h-7 text-white" strokeWidth={3} /> : <AlertCircle className="w-7 h-7 text-white" />}
                </div>
                <div className="ml-3">
                  <p className="text-gray-400 text-xs uppercase tracking-wider">Battery Status</p>
                  <p className={`text-sm font-medium ${isHealthy ? 'text-green-400' : 'text-red-400'}`}>
                    {isHealthy ? '38 MONTH' : '4 MONTH'} WARRANTY
                  </p>
                </div>
              </div>
              <button className="w-10 h-10 rounded-full bg-gray-800/50 flex items-center justify-center">
                <Bell className="w-5 h-5 text-gray-400" />
              </button>
            </div>
            
            <h2 className={`text-4xl font-bold ${isHealthy ? 'text-green-400' : 'text-yellow-400'} mb-8`}>
              {isHealthy ? 'Great' : 'Poor'}
            </h2>
            
            <div className="relative w-52 h-52 mx-auto mb-8">
              <svg className="w-52 h-52 transform -rotate-90">
                <circle
                  cx="104"
                  cy="104"
                  r="96"
                  stroke="rgba(255,255,255,0.05)"
                  strokeWidth="16"
                  fill="none"
                />
                <circle
                  cx="104"
                  cy="104"
                  r="96"
                  stroke={isHealthy ? '#10b981' : '#facc15'}
                  strokeWidth="16"
                  fill="none"
                  strokeDasharray={`${batteryHealth * 6} 600`}
                  strokeLinecap="round"
                  className={`transition-all duration-1000 ${animateProgress ? 'animate-draw-circle' : ''}`}
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className={`text-6xl font-bold ${isHealthy ? 'text-green-400' : 'text-yellow-400'}`}>
                  {batteryHealth}%
                </span>
              </div>
            </div>
            
            <div className="flex items-center justify-center space-x-4">
              <InterstrateBattery size="large" />
              <div className={`w-14 h-20 ${isHealthy ? 'bg-green-500' : 'bg-yellow-400'} rounded-xl shadow-lg relative overflow-hidden`}>
                <div className={`absolute bottom-0 left-0 right-0 bg-white/20 transition-all duration-1000`} 
                     style={{height: `${batteryHealth}%`}}></div>
              </div>
            </div>
          </div>
          
          {!isHealthy && (
            <div className="mt-6 bg-gradient-to-r from-yellow-900/30 to-orange-900/30 backdrop-blur rounded-2xl p-5 border border-yellow-800/30">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-white font-bold text-lg">Power Up Your Savings!</h3>
                <span className="bg-yellow-500 text-black text-xs font-bold px-3 py-1.5 rounded-full">
                  20% OFF
                </span>
              </div>
              <p className="text-gray-300 text-sm mb-4">Get 20% OFF on all new battery orders.</p>
              <button
                onClick={() => navigateToScreen('delivery')}
                className="w-full bg-yellow-500 text-black py-3 rounded-xl font-bold hover:bg-yellow-400 transition-all"
              >
                Buy now
              </button>
            </div>
          )}
          
          <div className="mt-6 flex items-center justify-between">
            <div>
              <p className="text-white font-medium text-lg">Toyota Land Cruiser</p>
              <p className="text-green-400 text-sm flex items-center mt-1">
                <Check className="w-4 h-4 mr-1" /> Connected
              </p>
            </div>
            <button className="px-6 py-2 border border-gray-700 rounded-xl text-gray-400 text-sm hover:border-gray-600 transition-colors">
              Edit
            </button>
          </div>
        </div>
        
        <BottomNav activeTab="home" />
      </div>
    );
  }

  function BottomNav({ activeTab }) {
    const tabs = [
      { id: 'home', icon: Home, label: 'Home' },
      { id: 'order', icon: Package, label: 'Order battery' },
      { id: 'change', icon: RefreshCw, label: 'Change device' },
      { id: 'profile', icon: User, label: 'Profile' }
    ];
    
    return (
      <div className="absolute bottom-0 left-0 right-0 bg-black border-t border-gray-800">
        <div className="flex justify-around items-center py-2">
          {tabs.map(tab => {
            const Icon = tab.icon;
            const isActive = tab.id === activeTab;
            return (
              <button key={tab.id} onClick={handleInteraction} className="flex flex-col items-center py-2 px-3">
                <Icon className={`w-5 h-5 mb-1 ${isActive ? 'text-green-400' : 'text-gray-500'}`} />
                <span className={`text-xs ${isActive ? 'text-green-400' : 'text-gray-500'}`}>{tab.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center space-x-6">
      <style jsx>{`
        @keyframes slide-down {
          from {
            transform: translateY(-100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        @keyframes progress {
          from { width: 0; }
          to { width: 60%; }
        }
        @keyframes draw-circle {
          from { stroke-dasharray: 0 600; }
        }
        .animate-slide-down {
          animation: slide-down 0.3s ease-out;
        }
        .animate-progress {
          animation: progress 3s ease-out;
        }
        .animate-draw-circle {
          animation: draw-circle 1s ease-out forwards;
        }
      `}</style>
      
      {/* Phone Frame */}
      <div className="relative w-[390px] h-[844px] bg-gray-900 rounded-[60px] p-[10px] shadow-2xl">
        <div className="absolute top-[10px] left-1/2 transform -translate-x-1/2 w-[150px] h-[30px] bg-black rounded-b-3xl"></div>
        <div className="relative w-full h-full bg-black rounded-[50px] overflow-hidden">
          {/* Dynamic Island */}
          <div className="absolute top-3 left-1/2 transform -translate-x-1/2 w-32 h-8 bg-black rounded-full z-20"></div>
          
          {/* App Content */}
          <div className="h-full">
            {screens[currentScreen]}
          </div>
        </div>
      </div>

      {/* Vertical Carousel Controls */}
      <div className="flex flex-col items-center text-white bg-gray-900/50 backdrop-blur-md p-3 rounded-full space-y-4 pointer-events-auto">
        <button onClick={() => navigate('prev')} className="p-2 rounded-full hover:bg-gray-700 transition-colors cursor-pointer">
          <ChevronLeft size={20} className="rotate-90" />
        </button>
        <div className="flex flex-col items-center space-y-2 pointer-events-auto">
        {screenOrder.map((screen) => (
            <button key={screen} onClick={() => navigateToScreen(screen)} className={`w-2.5 h-2.5 rounded-full transition-all duration-300 cursor-pointer ${currentScreen === screen ? 'bg-green-500 scale-125' : 'bg-gray-600 hover:bg-gray-500'}`}></button>
          ))}
        </div>
        <button onClick={() => navigate('next')} className="p-2 rounded-full hover:bg-gray-700 transition-colors cursor-pointer">
          <ChevronRight size={20} className="rotate-90" />
        </button>
        <button 
          onClick={() => setIsAutoPlaying(!isAutoPlaying)} 
          className={`p-2 rounded-full transition-colors ${isAutoPlaying ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'}`}
          title={isAutoPlaying ? 'Pause Autoplay' : 'Start Autoplay'}
        >
          {isAutoPlaying ? <Pause size={16} /> : <Play size={16} />}
        </button>
      </div>
    </div>
  );
};

// Tracking System Components
const TrackingSystemSVG = () => {
  const [carPosition, setCarPosition] = useState({ x: 300, y: 100, rotation: 180 });
  const [carRoute, setCarRoute] = useState('to-red');
  const [carProgress, setCarProgress] = useState(0);
  const [activatedBuildings, setActivatedBuildings] = useState(new Set());

  const buildings = [
    { id: 'north', x: 300, y: 100, color: '#4299e1', icon: '⚡', name: 'North Station' },
    { id: 'east', x: 500, y: 300, color: '#ed8936', icon: '🔧', name: 'East Service' },
    { id: 'south', x: 300, y: 500, color: '#f56565', icon: '📦', name: 'South Depot' },
    { id: 'west', x: 100, y: 300, color: '#ecc94b', icon: '🏢', name: 'West HQ' }
  ];

  useEffect(() => {
    const updateCar = () => {
      let x, y, rotation, newProgress = carProgress;
      
      switch(carRoute) {
        case 'to-red':
          x = 300;
          y = 100 + (400 * carProgress);
          rotation = 180;
          newProgress += 0.008;
          if (newProgress >= 1) {
            setCarRoute('at-red');
            newProgress = 0;
          }
          break;
        case 'at-red':
          x = 250;
          y = 500;
          rotation = 90;
          newProgress += 0.02;
          if (newProgress >= 1) {
            setCarRoute('to-blue');
            newProgress = 0;
          }
          break;
        case 'to-blue':
          x = 300;
          y = 500 - (400 * carProgress);
          rotation = 0;
          newProgress += 0.008;
          if (newProgress >= 1) {
            setCarRoute('at-blue');
            newProgress = 0;
          }
          break;
        case 'at-blue':
          x = 350;
          y = 100;
          rotation = 90;
          newProgress += 0.02;
          if (newProgress >= 1) {
            setCarRoute('to-red');
            newProgress = 0;
          }
          break;
      }
      
      setCarPosition({ x, y, rotation });
      setCarProgress(newProgress);

      // Check which buildings should be activated
      const newActivatedBuildings = new Set();
      buildings.forEach(building => {
        const distance = Math.sqrt(Math.pow(x - building.x, 2) + Math.pow(y - building.y, 2));
        if (distance < 60) {
          newActivatedBuildings.add(building.id);
        }
      });
      setActivatedBuildings(newActivatedBuildings);
    };

    const interval = setInterval(updateCar, 16); // ~60 FPS
    return () => clearInterval(interval);
  }, [carRoute, carProgress]);

  return (
    <svg width="1000" height="500" viewBox="0 0 600 600" className="w-full h-full">
      {/* Background terrain */}
      <defs>
        <radialGradient id="grassGradient">
          <stop offset="0%" stopColor="#68d391"/>
          <stop offset="100%" stopColor="#48bb78"/>
        </radialGradient>
        <pattern id="grassPattern" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
          <rect width="40" height="40" fill="url(#grassGradient)"/>
          <circle cx="5" cy="5" r="2" fill="#38a169" opacity="0.3"/>
          <circle cx="15" cy="25" r="1.5" fill="#38a169" opacity="0.3"/>
          <circle cx="30" cy="10" r="1.8" fill="#38a169" opacity="0.3"/>
          <circle cx="25" cy="35" r="2" fill="#38a169" opacity="0.3"/>
        </pattern>
      </defs>
      
      {/* Organic terrain shape */}
      <path d="M 300 20 Q 450 40, 550 150 Q 580 300, 520 420 Q 400 550, 250 560 Q 100 540, 50 400 Q 20 250, 80 120 Q 180 30, 300 20"
            fill="url(#grassPattern)" stroke="none"/>
      
      {/* Roads */}
      <circle cx="300" cy="300" r="200" fill="none" stroke="#4a5568" strokeWidth="30" opacity="0.8"/>
      <circle cx="300" cy="300" r="200" fill="none" stroke="#fbbf24" strokeWidth="2" strokeDasharray="10, 10" opacity="0.6"/>
      <rect x="285" y="100" width="30" height="400" fill="#4a5568" opacity="0.8"/>
      <line x1="300" y1="100" x2="300" y2="500" stroke="#fbbf24" strokeWidth="2" strokeDasharray="10, 10" opacity="0.6"/>
      <rect x="100" y="285" width="400" height="30" fill="#4a5568" opacity="0.8"/>
      <line x1="100" y1="300" x2="500" y2="300" stroke="#fbbf24" strokeWidth="2" strokeDasharray="10, 10" opacity="0.6"/>
      
      {/* Trees and bushes */}
      <g opacity="0.7">
        <circle cx="150" cy="80" r="25" fill="#2d7a3e"/>
        <rect x="145" y="95" width="10" height="20" fill="#8b4513"/>
        <circle cx="450" cy="90" r="20" fill="#38a169"/>
        <rect x="445" y="105" width="10" height="18" fill="#8b4513"/>
        <circle cx="520" cy="200" r="22" fill="#2d7a3e"/>
        <rect x="515" y="217" width="10" height="18" fill="#8b4513"/>
        <circle cx="80" cy="200" r="23" fill="#38a169"/>
        <rect x="75" y="218" width="10" height="18" fill="#8b4513"/>
        <circle cx="100" cy="450" r="25" fill="#2d7a3e"/>
        <rect x="95" y="468" width="10" height="20" fill="#8b4513"/>
        <circle cx="500" cy="480" r="20" fill="#38a169"/>
        <rect x="495" y="495" width="10" height="18" fill="#8b4513"/>
        <ellipse cx="200" cy="520" rx="20" ry="15" fill="#48bb78"/>
        <ellipse cx="400" cy="530" rx="18" ry="12" fill="#48bb78"/>
        <ellipse cx="50" cy="350" rx="15" ry="12" fill="#48bb78"/>
        <ellipse cx="550" cy="350" rx="17" ry="13" fill="#48bb78"/>
      </g>
      
      {/* Buildings */}
      {buildings.map(building => {
        const isActive = activatedBuildings.has(building.id);
        return (
          <g key={building.id} className={`transition-all duration-300 ${isActive ? 'drop-shadow-lg' : ''}`}>
            <rect 
              x={building.x - 20} 
              y={building.y - 30} 
              width="40" 
              height={building.id === 'north' || building.id === 'south' ? "60" : "55"} 
              fill={building.color}
              style={{
                filter: isActive ? `drop-shadow(0 0 20px ${building.color})` : 'none'
              }}
            />
            <rect x={building.x - 15} y={building.y - 20} width="8" height="8" fill={isActive ? '#ffd700' : '#ffffff'} opacity="0.8"/>
            <rect x={building.x + 2} y={building.y - 20} width="8" height="8" fill={isActive ? '#ffd700' : '#ffffff'} opacity="0.8"/>
            <rect x={building.x - 15} y={building.y - 5} width="8" height="8" fill={isActive ? '#ffd700' : '#ffffff'} opacity="0.8"/>
            <rect x={building.x + 2} y={building.y - 5} width="8" height="8" fill={isActive ? '#ffd700' : '#ffffff'} opacity="0.8"/>
            <rect x={building.x - 6} y={building.y + 10} width="12" height={building.id === 'north' || building.id === 'south' ? "20" : "15"} fill="#2d3748"/>
            <text x={building.x} y={building.id === 'north' ? building.y - 35 : building.id === 'south' ? building.y + 45 : building.y - 35} 
                  textAnchor="middle" fill={building.color} fontSize="12" fontWeight="bold">
              {building.icon}
            </text>
          </g>
        );
      })}
      
      {/* Car */}
      <g transform={`translate(${carPosition.x}, ${carPosition.y}) rotate(${carPosition.rotation})`}>
        <rect x="-8" y="-15" width="16" height="30" fill="#9f7aea" rx="3"/>
        <rect x="-5" y="-8" width="10" height="6" fill="#e9d8fd"/>
        <rect x="-5" y="2" width="10" height="6" fill="#e9d8fd"/>
        <circle cx="-8" cy="-8" r="3" fill="#2d3748"/>
        <circle cx="8" cy="-8" r="3" fill="#2d3748"/>
        <circle cx="-8" cy="8" r="3" fill="#2d3748"/>
        <circle cx="8" cy="8" r="3" fill="#2d3748"/>
        <circle cx="-3" cy="-13" r="2" fill="#fbbf24"/>
        <circle cx="3" cy="-13" r="2" fill="#fbbf24"/>
      </g>
      
    </svg>
  );
};

const NotificationPanel = () => {
  const [notifications, setNotifications] = useState([]);
  const [milesRemaining, setMilesRemaining] = useState(500);
  const [isVisible, setIsVisible] = useState(false);
  const [animationPhase, setAnimationPhase] = useState('slideIn'); // 'slideIn', 'visible', 'fadeOut'

  useEffect(() => {
    const updateNotifications = () => {
      const newMilesRemaining = 400 + (Math.sin(Date.now() / 5000) * 100);
      setMilesRemaining(Math.floor(newMilesRemaining));
      
      const newNotifications = [
        {
          id: 'bob',
          type: 'bob',
          icon: '🚗',
          title: 'BOB',
          time: 'now',
          body: `Car needs battery change in ${Math.floor(newMilesRemaining)} miles. Would you like to schedule an appointment?`,
          actions: ['Yes', 'No'],
          highlighted: Math.sin(Date.now() / 2000) > 0.5
        },
        {
          id: 'interstate',
          type: 'interstate',
          icon: '🏢',
          title: 'INTERSTATE',
          time: '2s ago',
          body: `Bob most likely to be at Red or Blue buildings for his battery change in the next ${Math.floor(newMilesRemaining)} miles.`,
          actions: ['Send Promotion'],
          highlighted: false
        }
      ];
      
      setNotifications(newNotifications);
    };

    // Animation cycle: slide in, stay visible, fade out, repeat
    const animationCycle = () => {
      // Slide in
      setAnimationPhase('slideIn');
      setIsVisible(true);
      updateNotifications();
      
      setTimeout(() => {
        setAnimationPhase('visible');
      }, 500);
      
      // Fade out after 8 seconds of visibility
      setTimeout(() => {
        setAnimationPhase('fadeOut');
      }, 8500);
      
      // Hide completely after fade out
      setTimeout(() => {
        setIsVisible(false);
      }, 9500);
    };

    // Start immediately
    animationCycle();
    
    // Repeat every 10 seconds
    const cycleInterval = setInterval(animationCycle, 10000);
    
    // Update notifications every 2 seconds when visible
    const updateInterval = setInterval(() => {
      if (isVisible) {
        updateNotifications();
      }
    }, 2000);
    
    return () => {
      clearInterval(cycleInterval);
      clearInterval(updateInterval);
    };
  }, [isVisible]);

  if (!isVisible) return null;

  const getAnimationClasses = () => {
    switch (animationPhase) {
      case 'slideIn':
        return 'transform -translate-y-4 opacity-0 animate-in slide-in-from-top-4 fade-in duration-500';
      case 'visible':
        return 'transform translate-y-0 opacity-100 transition-all duration-300';
      case 'fadeOut':
        return 'transform translate-y-0 opacity-0 transition-all duration-1000';
      default:
        return '';
    }
  };

  return (
    <div className={`space-y-3 ${getAnimationClasses()}`}>
      {notifications.map((notification, index) => (
        <div 
          key={notification.id}
          className={`bg-white p-3 shadow-lg border-l-4 transition-all duration-300 rounded-xl ${
            notification.type === 'bob' ? 'border-purple-500' : 'border-green-500'
          } ${notification.highlighted ? 'shadow-purple-500/50 bg-gradient-to-r from-purple-50 to-white scale-105' : ''}`}
          style={{
            animationDelay: `${index * 100}ms`
          }}
        >
          <div className="flex items-center mb-2">
            <div className={`w-5 h-5 rounded-full flex items-center justify-center text-xs mr-2 ${
              notification.type === 'bob' ? 'bg-purple-100 text-purple-600' : 'bg-green-100 text-green-600'
            }`}>
              {notification.icon}
            </div>
            <div className="font-semibold text-gray-800 text-xs">{notification.title}</div>
            <div className="text-xs text-gray-500 ml-auto">{notification.time}</div>
          </div>
          <div className="text-xs text-gray-700 mb-2 ml-7">{notification.body}</div>
          <div className="flex gap-2 ml-7">
            {notification.actions.map((action, actionIndex) => (
              <button 
                key={actionIndex}
                className={`px-2 py-1 rounded-md text-xs font-medium transition-colors ${
                  actionIndex === 0 
                    ? (notification.type === 'bob' ? 'bg-purple-500 text-white hover:bg-purple-600' : 'bg-green-500 text-white hover:bg-green-600')
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {action}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

const CarParallaxAnimation = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentScene, setCurrentScene] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [showWistiaVideo, setShowWistiaVideo] = useState(false);
  const [activeChart, setActiveChart] = useState('sales');
  const [currentFeatureIndex, setCurrentFeatureIndex] = useState(0);
  const [isCarouselAutoplayPaused, setIsCarouselAutoplayPaused] = useState(false);
  const [topLayer, setTopLayer] = useState('card'); // 'card' or 'animation'
  const [batteryCapacity, setBatteryCapacity] = useState(100);
  const [batteryTimeToFailure, setBatteryTimeToFailure] = useState(24);
  const [isAnimationPlaying, setIsAnimationPlaying] = useState(true);
  const resetAnimationRef = useRef(null);
  const containerRef = useRef(null);
  const scenes = ['drive', 'breakdown', 'transport', 'repair', 'interstate', 'onward'];
  const sceneLabels = ['Drive', 'Breakdown', 'Transport', 'Repair', 'Interstate', 'Onward'];
  
  // Features for the carousel
  const features = [
    {
      title: 'Active Monitoring',
      description: 'Provide active monitoring with a battery health gauge',
      icon: (
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="1" y="6" width="6" height="10" rx="1"/>
          <rect x="9" y="4" width="6" height="14" rx="1"/>
          <rect x="17" y="2" width="6" height="18" rx="1"/>
        </svg>
      )
    },
    {
      title: 'Warranty Tracking',
      description: 'Keep track of warranty purchased with battery',
      icon: (
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
          <path d="m9 12 2 2 4-4"/>
        </svg>
      )
    },
    {
      title: 'Health Tips',
      description: 'Provide tips to maintain battery health based on usage patterns and habits',
      icon: (
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386L9.663 17z"/>
        </svg>
      )
    },
    {
      title: 'Health Alerts',
      description: 'Provide alerts in event of a battery health issue',
      icon: (
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/>
          <line x1="12" y1="9" x2="12" y2="13"/>
          <line x1="12" y1="17" x2="12.01" y2="17"/>
        </svg>
      )
    },
    {
      title: 'Easy Scheduling',
      description: 'Provide easy in app scheduling option to exchange battery',
      icon: (
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
          <line x1="16" y1="2" x2="16" y2="6"/>
          <line x1="8" y1="2" x2="8" y2="6"/>
          <line x1="3" y1="10" x2="21" y2="10"/>
        </svg>
      )
    },
    {
      title: 'Warranty Protection',
      description: 'Get usage tips and alerts that help you stay within warranty guidelines.',
      icon: (
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
          <path d="M12 8v4"/>
          <path d="M12 16h.01"/>
        </svg>
      )
    }
  ];

  // Initialize scene from URL parameter
  useEffect(() => {
    const sceneParam = searchParams.get('scene');
    if (sceneParam) {
      const sceneIndex = scenes.indexOf(sceneParam.toLowerCase());
      if (sceneIndex !== -1) {
        setCurrentScene(sceneIndex);
        scrollToScene(sceneIndex);
      }
    }
  }, []);

  // Update URL when scene changes
  useEffect(() => {
    const currentSceneName = scenes[currentScene];
    if (currentSceneName) {
      setSearchParams({ scene: currentSceneName });
    }
  }, [currentScene, setSearchParams]);

  // Reset battery state when leaving breakdown scene, start animation when entering
  useEffect(() => {
    if (currentScene !== 1) {
      // Reset when not on breakdown scene
      setBatteryCapacity(100);
      setBatteryTimeToFailure(24);
      setIsAnimationPlaying(false);
    } else {
      // Start animation when entering breakdown scene
      setIsAnimationPlaying(true);
    }
  }, [currentScene]);

  // Animation control functions
  const handlePlayPause = () => {
    setIsAnimationPlaying(prev => !prev);
  };

  const handleRestart = () => {
    if (resetAnimationRef.current) {
      resetAnimationRef.current();
    }
    // Reset battery capacity to 100% and time to 24 hours
    setBatteryCapacity(100);
    setBatteryTimeToFailure(24);
    setIsAnimationPlaying(true);
  };

  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;
      
      const scrollTop = window.scrollY;
      const windowHeight = window.innerHeight;
      const documentHeight = containerRef.current.offsetHeight - windowHeight;
      const progress = Math.min(Math.max(scrollTop / documentHeight, 0), 1);
      
      setScrollProgress(progress);
      const calculatedScene = Math.floor(progress * scenes.length);
      // Prevent scrolling past the last scene (Departure)
      setCurrentScene(Math.min(calculatedScene, scenes.length - 1));
    };

    const handleKeyDown = (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        const prevScene = Math.max(0, currentScene - 1);
        scrollToScene(prevScene);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        const nextScene = Math.min(scenes.length - 1, currentScene + 1);
        scrollToScene(nextScene);
      }
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [currentScene, scenes.length]);

  const scrollToScene = (index) => {
    if (!containerRef.current) return;
    
    const windowHeight = window.innerHeight;
    const documentHeight = containerRef.current.offsetHeight - windowHeight;
    
    // Calculate the target progress for the desired scene
    const targetProgress = (index + 0.5) / scenes.length; // Center of the scene
    const targetScrollPosition = targetProgress * documentHeight;
    
    window.scrollTo({
      top: targetScrollPosition,
      behavior: 'smooth'
    });
  };

  // Reset carousel autoplay when entering Transport scene
  useEffect(() => {
    if (currentScene === 2) {
      setIsCarouselAutoplayPaused(false); // Resume autoplay when entering Transport scene
    }
  }, [currentScene]);

  // Carousel auto-advance effect
  useEffect(() => {
    if (currentScene === 2 && !isCarouselAutoplayPaused) { // Only run carousel on Transport scene when not paused
      const interval = setInterval(() => {
        setCurrentFeatureIndex((prevIndex) => (prevIndex + 1) % features.length);
      }, 4000);
      return () => clearInterval(interval);
    }
  }, [currentScene, features.length, isCarouselAutoplayPaused]);

  const nextFeature = () => {
    setIsCarouselAutoplayPaused(true); // Pause autoplay when user manually navigates
    setCurrentFeatureIndex((prevIndex) => (prevIndex + 1) % features.length);
  };

  const prevFeature = () => {
    setIsCarouselAutoplayPaused(true); // Pause autoplay when user manually navigates
    setCurrentFeatureIndex((prevIndex) => (prevIndex - 1 + features.length) % features.length);
  };

  const goToFeature = (index) => {
    setIsCarouselAutoplayPaused(true); // Pause autoplay when user clicks dots
    setCurrentFeatureIndex(index);
  };

  return (
    <div ref={containerRef} className="relative bg-gradient-to-b from-sky-300 via-sky-400 to-sky-500" style={{ height: '500vh' }}>
      {/* Fixed Navigation */}
      <div className="fixed top-8 left-1/2 transform -translate-x-1/2 z-50 bg-gray-900/90 backdrop-blur-md rounded-full px-6 py-3 shadow-2xl border border-green-500/30">
        <div className="flex gap-4">
          {sceneLabels.map((sceneLabel, index) => (
            <button
              key={index}
              onClick={() => scrollToScene(index)}
              className={`px-4 py-2 rounded-full transition-all duration-300 font-semibold tracking-wide min-w-[80px] flex items-center gap-2 ${
                currentScene === index 
                  ? 'bg-gradient-to-r from-blue-600 to-green-600 text-white scale-110 shadow-lg' 
                  : 'bg-gray-700/50 hover:bg-gray-600/70 text-gray-300 hover:text-white'
              }`}
            >
              <span className="w-5 h-5 bg-current opacity-20 rounded-full flex items-center justify-center text-xs font-bold">
                {index + 1}
              </span>
              {sceneLabel}
            </button>
          ))}
        </div>
      </div>

      {/* Far Background Layers (Sky & Sun) - z-0 */}
      <div className="fixed inset-0 z-0">
        {/* Sun */}
        <div 
          className="absolute w-24 h-24 bg-yellow-400 rounded-full shadow-2xl"
          style={{
            top: '10%',
            left: `${20 + scrollProgress * 60}%`,
            transform: `translateY(${scrollProgress * 100}px)`
          }}
        />
        
        {/* Clouds */}
        <div 
          className="absolute top-20 w-full"
          style={{ transform: `translateX(${-scrollProgress * 200}px)` }}
        >
          <div className="absolute left-10 w-32 h-16 bg-white rounded-full opacity-80" />
          <div className="absolute left-48 w-40 h-20 bg-white rounded-full opacity-70" />
          <div className="absolute left-96 w-36 h-18 bg-white rounded-full opacity-75" />
        </div>

      </div>

      {/* Scene Content Cards (z-10) */}
      <div
        className="fixed inset-0 pointer-events-none"
        style={{ zIndex: topLayer === 'card' ? 40 : 10 }}
      >
        {/* Scene 1: Drive Info */}
        <div
          className={`fixed left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 transition-all duration-700 ease-in-out ${currentScene === 0 ? 'pointer-events-auto' : 'pointer-events-none'}`}
          onClick={() => setTopLayer('card')}
          style={{
            opacity: currentScene === 0 ? 1 : 0,
            visibility: currentScene === 0 ? 'visible' : 'hidden',
            transform: `translateX(-50%) translateY(-50%) scale(${currentScene === 0 ? 1 : 0.9})`,
            width: '90%',
            maxWidth: '1200px',
          }}
        >
          <div className="bg-gray-900/90 backdrop-blur-md rounded-3xl shadow-2xl p-8 border border-green-500/30 relative overflow-hidden">
            {/* Subtle Pattern Overlay */}
            <div className="absolute inset-0 opacity-5">
              <div className="absolute inset-0" style={{
                backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(16, 185, 129, 0.1) 35px, rgba(16, 185, 129, 0.1) 70px)`
              }}></div>
            </div>

            {/* Main Content Layout */}
            <div className="relative z-10 grid grid-cols-12 gap-8 items-center">
              
              {/* Left Side: Gibbs Whiteboard */}
              <div className="col-span-4">
                <div className="relative">
                  {/* Stylized Whiteboard with Image */}
                  <div className="bg-white rounded-2xl transform -rotate-2 shadow-xl border-4 border-gray-300 relative overflow-hidden">
                    <div className="p-4">
                      <img 
                        src="/gibbs.png" 
                        alt="Gibbs Game Plan Whiteboard" 
                        className="w-full h-auto rounded-lg"
                      />
                    </div>
                    {/* Marker effect */}
                    <div className="absolute -top-1 -right-1 w-6 h-1 bg-red-500 rounded-full"></div>
                  </div>
                </div>
              </div>

              {/* Right Side: Main Content */}
              <div className="col-span-8">
              {/* Header with Icon */}
              <div className="flex items-center justify-center mb-6">
                <div className="bg-gradient-to-br from-green-500 to-green-600 p-4 rounded-2xl shadow-lg transform -rotate-3 hover:rotate-0 transition-transform">
                  <Trophy className="w-10 h-10 text-white" />
                </div>
              </div>

              {/* Title */}
              <h1 className="text-6xl lg:text-8xl font-black text-center mb-6 text-white italic tracking-wide leading-tight">
                #1 in USA: The Foundation
              </h1>
              
              {/* Subtitle */}
              <p className="text-lg text-green-400 text-center mb-8 font-light">
                Interstate excels by dominating the fundamentals in a commodity market
              </p>

              {/* Key Points Grid */}
              <div className="grid grid-cols-1 gap-4 mb-8">
                <div className="group">
                  <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-6 transform transition-all duration-300 hover:scale-105 hover:shadow-xl border border-green-500/20 hover:border-green-400/40">
                    <div className="flex items-center gap-3">
                      <div className="bg-green-600 p-3 rounded-xl shadow-md">
                        <MapPin className="w-8 h-8 text-white" />
                      </div>
                      <div>
                        <h3 className="font-bold text-white text-xl">Dominating Distribution</h3>
                        <p className="text-gray-400 text-lg">Highest density nationwide coverage</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="group">
                  <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-6 transform transition-all duration-300 hover:scale-105 hover:shadow-xl border border-green-500/20 hover:border-green-400/40">
                    <div className="flex items-center gap-3">
                      <div className="bg-green-600 p-3 rounded-xl shadow-md">
                        <Award className="w-8 h-8 text-white" />
                      </div>
                      <div>
                        <h3 className="font-bold text-white text-xl">Brand Awareness</h3>
                        <p className="text-gray-400 text-lg">Highest unaided brand recognition</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="group">
                  <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-6 transform transition-all duration-300 hover:scale-105 hover:shadow-xl border border-green-500/20 hover:border-green-400/40">
                    <div className="flex items-center gap-3">
                      <div className="bg-green-600 p-3 rounded-xl shadow-md">
                        <Users className="w-8 h-8 text-white" />
                      </div>
                      <div>
                        <h3 className="font-bold text-white text-xl">Expanding Social Reach</h3>
                        <p className="text-gray-400 text-lg">Growing our digital footprint</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Call to Action */}
              <div className="text-center">
                <p className="text-lg text-gray-300 mb-2">Now is the time to run the</p>
                <h2 className="text-3xl font-black text-white italic tracking-wide">Counter Trey</h2>
                <div className="mt-4 inline-flex items-center gap-2 text-green-400 font-medium">
                  <Zap className="w-4 h-4" />
                  <span className="italic text-sm">Outrageously Dependable®</span>
                </div>
              </div>
              </div> {/* Close right column */}
            </div> {/* Close grid */}
          </div>
        </div>


        {/* Scene 2: Breakdown Content - Single Integrated Card */}
        <div
            className={`absolute inset-0 flex items-center justify-center px-12 pt-24 pb-32 transition-all duration-700 ease-in-out ${currentScene === 1 ? 'pointer-events-auto' : 'pointer-events-none'}`}
            onClick={() => setTopLayer('card')}
            style={{ 
              opacity: currentScene === 1 ? 1 : 0,
              visibility: currentScene === 1 ? 'visible' : 'hidden',
            }}
        >
                {/* Combined Single Card */}
                <BatteryCard 
                    className="text-white shadow-2xl p-8 w-full max-w-7xl pointer-events-auto"
                    reserveCapacity={batteryCapacity}
                    setReserveCapacity={setBatteryCapacity}
                    timeToFailure={batteryTimeToFailure}
                    setTimeToFailure={setBatteryTimeToFailure}
                    isPlaying={isAnimationPlaying}
                    onReset={resetAnimationRef}
                >
                    <div>
                        
                        {/* Header */}
                        <h2 className="text-5xl font-bold text-white mb-3 text-center tracking-tight">Gameplan: The Counter Trey</h2>
                        <p className="text-xl text-green-300 text-center mb-8 font-light">Converting commodity batteries into subscription revenue through AI-enabled predictive maintenance</p>
                        
                        {/* Main Content Grid */}
                        <div className="grid grid-cols-12 gap-8 items-start">
                            
                            {/* Left Content - Strategy Points */}
                            <div className="col-span-6 space-y-6">
                                
                                {/* The 3-Phase Strategy */}
                                <div>
                                    <h3 className="text-2xl font-bold text-green-400 mb-4">The 3-Phase Strategy</h3>
                                    
                                    <div className="space-y-4">
                                        <div className="flex items-start gap-4">
                                            <div className="bg-green-600 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0">1</div>
                                            <div>
                                                <h4 className="text-xl font-bold text-white mb-2">The Counter Trey</h4>
                                                <p className="text-gray-300 leading-relaxed">Take a commodity product and create something new, special, and differentiated. Launch <span className="text-green-400 font-semibold">AI-enabled batteries</span> across all segments in 2026 with messaging focused on <span className="bg-green-600/20 px-2 py-1 rounded text-green-300">Safety</span>, <span className="bg-green-600/20 px-2 py-1 rounded text-green-300">Convenience</span>, and <span className="bg-green-600/20 px-2 py-1 rounded text-green-300">Smart Technology</span>.</p>
                                            </div>
                                        </div>
                                        
                                        <div className="flex items-start gap-4">
                                            <div className="bg-green-600 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0">2</div>
                                            <div>
                                                <h4 className="text-xl font-bold text-white mb-2">The Predictive Deliverable</h4>
                                                <p className="text-gray-300 leading-relaxed">While competitors chase premium battery features, we deliver <span className="bg-green-600/20 px-2 py-1 rounded text-green-300">predictive maintenance</span>. Smart Batteries maintain themselves, alerting users before failure through our smartphone app.</p>
                                            </div>
                                        </div>
                                        
                                        <div className="flex items-start gap-4">
                                            <div className="bg-green-600 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0">3</div>
                                            <div>
                                                <h4 className="text-xl font-bold text-white mb-2">The Business Model Shift</h4>
                                                <p className="text-gray-300 leading-relaxed">Convert the industry's run-to-failure model into a <span className="bg-green-600/20 px-2 py-1 rounded text-green-300">subscription business</span> leveraging embedded AI, smartphone connectivity, warranty protection, and app capabilities.</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Center Content - Video */}
                            <div className="col-span-4">
                                <div className="bg-gray-800/50 rounded-2xl p-6 border border-green-500/20">
                                    <div className="text-center mb-4">
                                        <div className="text-white text-lg font-bold mb-4">
                                            {showWistiaVideo ? "How Gibbs Did It" : ""}
                                        </div>
                                    </div>
                                    
                                    {!showWistiaVideo ? (
                                        <Player
                                            component={CounterTreyPlay}
                                            durationInFrames={240}
                                            compositionWidth={400}
                                            compositionHeight={420}
                                            fps={30}
                                            style={{ width: '100%', height: '350px', borderRadius: '12px', overflow: 'hidden' }}
                                            controls autoPlay={currentScene === 1} loop clickToPlay
                                            acknowledgeRemotionLicense={true}
                                            allowFullscreen showVolumeControls
                                        />
                                    ) : (
                                        <WistiaVideo mediaId="pm1w36bgos" aspect="1.498" style={{ width: '100%', height: '350px', borderRadius: '12px' }} />
                                    )}
                                    
                                    <div className="mt-4 flex justify-center">
                                        <button
                                            onClick={() => setShowWistiaVideo(!showWistiaVideo)}
                                            className="bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700 text-white font-bold py-3 px-6 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg flex items-center justify-center gap-2"
                                        >
                                            {showWistiaVideo ? (<><ArrowLeft size={20} /> Back to Counter Trey</>) : (<><Play size={20} /> See How Gibbs Did It</>)}
                                        </button>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Right Content - Battery Indicators */}
                            <div className="col-span-2">
                                <BatteryIndicators 
                                    reserveCapacity={batteryCapacity}
                                    timeToFailure={batteryTimeToFailure}
                                    isPlaying={isAnimationPlaying}
                                    onPlayPause={handlePlayPause}
                                    onRestart={handleRestart}
                                />
                            </div>
                        </div>
                    </div>
                </BatteryCard>
        </div>

        {/* Scene 3: Pickup Content - Leveraging Subscriptions */}
        <div
            className={`absolute inset-0 flex items-center justify-center px-12 pt-24 pb-32 transition-all duration-700 ease-in-out ${currentScene === 2 ? 'pointer-events-auto' : 'pointer-events-none'}`}
            onClick={() => setTopLayer('card')}
            style={{ 
              opacity: currentScene === 2 ? 1 : 0,
              visibility: currentScene === 2 ? 'visible' : 'hidden',
            }}
        >
                {/* Single Comprehensive Card */}
                <div className="bg-slate-800 text-white rounded-2xl shadow-2xl p-8 border border-green-500/30 w-full max-w-5xl pointer-events-auto relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 to-green-900/20 pointer-events-none" />
                    <div className="relative z-10">
                        
                        {/* Header */}
                        <h1 className="text-white text-4xl font-bold text-center mb-6">
                            Predict Failures & Turn Buyers into Subscribers
                        </h1>

                        {/* Animation - Full Width */}
                        <div className="mb-4 relative">
                            <div className="bg-white rounded-2xl p-4 relative" style={{ minHeight: '130px' }}>
                                <TrackingSystemSVG />
                                {/* Floating Notifications */}
                                <div className="absolute top-4 right-4 w-80">
                                    <NotificationPanel />
                                </div>
                            </div>
                        </div>

                        {/* Bottom Row - Subscription Impact */}
                        <div>
                            <div className="bg-green-900/30 border border-green-500/30 rounded-xl p-4">
                                <div className="flex justify-between items-center">
                                    <div>
                                        <p className="text-green-400 text-sm font-medium">Subscription Impact</p>
                                        <p className="text-white text-4xl font-bold">47M</p>
                                        <p className="text-gray-300 text-sm">Additional Batteries</p>
                                    </div>
                                    <div className="text-center">
                                        <p className="text-green-400 text-3xl font-bold">+94%</p>
                                        <p className="text-gray-400 text-xs">vs. Standard Model</p>
                                    </div>
                                    <div className="bg-slate-800 rounded-lg p-3">
                                        <p className="text-gray-500 text-xs mb-1">10-Year Projection</p>
                                        <div className="flex gap-6">
                                            <div>
                                                <span className="text-gray-400 text-sm">Standard:</span>
                                                <span className="text-white font-bold text-lg ml-2">50M</span>
                                            </div>
                                            <div>
                                                <span className="text-gray-400 text-sm">Subscription:</span>
                                                <span className="text-white font-bold underline text-lg ml-2">97M</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
        </div>

        {/* Scene 4: Buy Interstate */}
        <div 
          className={`absolute inset-0 transition-all duration-700 flex flex-col justify-center items-center px-12 pt-24 pb-32 ${currentScene === 3 ? 'pointer-events-auto' : 'pointer-events-none'}`}
          onClick={() => setTopLayer('card')}
          style={{
            opacity: currentScene === 3 ? 1 : 0,
            visibility: currentScene === 3 ? 'visible' : 'hidden',
            transform: `scale(${currentScene === 3 ? 1 : 0.8})`,
            paddingTop: '60px',
          }}
        >
          <div 
            className="w-full max-w-7xl mx-auto px-8 pointer-events-auto transition-all duration-500 mb-8 bg-gray-900/90 backdrop-blur-md rounded-3xl shadow-2xl p-8 border border-green-500/30 relative"
            style={{ opacity: currentScene === 3 ? 1 : 0 }}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 to-green-900/20 pointer-events-none rounded-3xl" />
            <div className="relative">
              <h2 className="text-3xl font-bold text-white mb-6 text-center">Smart Battery Sales Impact</h2>
              
              {/* Tab Navigation */}
              <div className="flex justify-center mb-6">
                <div className="flex bg-gray-800/60 rounded-xl p-1 backdrop-blur-sm border border-green-500/30">
                  <button
                    onClick={() => setActiveChart('sales')}
                    className={`px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${
                      activeChart === 'sales'
                        ? 'bg-green-500 text-white shadow-lg'
                        : 'text-gray-300 hover:text-white hover:bg-gray-700/50'
                    }`}
                  >
                    Smart Battery Sales
                  </button>
                  <button
                    onClick={() => setActiveChart('customers')}
                    className={`px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${
                      activeChart === 'customers'
                        ? 'bg-green-500 text-white shadow-lg'
                        : 'text-gray-300 hover:text-white hover:bg-gray-700/50'
                    }`}
                  >
                    Cumulative AI Customers
                  </button>
                </div>
              </div>
              
              {/* Chart Display */}
              <div className="h-[400px]">
                {activeChart === 'sales' && <SalesChart />}
                {activeChart === 'customers' && <CustomersChart />}
              </div>
            </div>
          </div>
        </div>

        {/* Scene 5: Repair Content Overlay */}
        <div 
          className={`absolute inset-0 transition-all duration-700 flex items-center justify-center px-12 pt-24 pb-32 ${currentScene === 4 ? 'pointer-events-auto' : 'pointer-events-none'}`}
          onClick={() => setTopLayer('card')}
          style={{
            opacity: currentScene === 4 ? 1 : 0,
            visibility: currentScene === 4 ? 'visible' : 'hidden',
            transform: `scale(${currentScene === 4 ? 1 : 0.8})`,
          }}
        >
          <div className="bg-gray-900/90 backdrop-blur-md rounded-3xl shadow-2xl p-8 border border-green-500/30 w-full max-w-7xl pointer-events-auto relative overflow-hidden min-h-[600px]">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 to-green-900/20 pointer-events-none rounded-3xl" />
            <div className="relative z-10">
              <SubscriptionTransitionDashboard />
            </div>
          </div>
        </div>

        {/* Scene 6: Drive */}
        <div 
          className={`absolute inset-0 transition-all duration-700 flex items-center justify-center px-12 pt-24 pb-32 ${currentScene === 5 ? 'pointer-events-auto' : 'pointer-events-none'}`}
          onClick={() => setTopLayer('card')}
          style={{
            opacity: currentScene === 5 ? 1 : 0,
            visibility: currentScene === 5 ? 'visible' : 'hidden',
            transform: `scale(${currentScene === 5 ? 1 : 0.8})`,
          }}
        >
          <div className="bg-gray-900/90 backdrop-blur-md rounded-3xl shadow-2xl p-8 border border-green-500/30 w-full max-w-7xl pointer-events-auto relative overflow-hidden min-h-[600px]">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 to-green-900/20 pointer-events-none" />
            <div className="relative z-10">
              
              {/* Grid Layout */}
              <div className="grid grid-cols-12 gap-8 items-center">
                
                {/* Left Side - Title and Content */}
                <div className="col-span-5 space-y-4">
                  {/* Header with Icon */}
                  <div className="flex items-center justify-start">
                    <div className="bg-gradient-to-br from-green-500 to-green-600 p-3 rounded-2xl shadow-lg transform -rotate-3">
                      <Battery className="w-8 h-8 text-white" />
                    </div>
                  </div>

                  {/* Title */}
                  <div className="space-y-3">
                    <h1 className="text-5xl font-black text-white leading-tight tracking-tight">
                      Keep them
                    </h1>
                    <h1 className="text-5xl font-black text-green-400 leading-tight tracking-tight">
                      coming back
                    </h1>
                  </div>

                  {/* Subtitle */}
                  <p className="text-xl text-gray-300 leading-relaxed max-w-lg">
                    Smart Battery's AI-powered app creates lasting customer relationships through predictive maintenance and seamless battery replacement.
                  </p>

                  {/* Key Benefits */}
                  <div className="space-y-4 pt-4">
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                      <span className="text-gray-300 text-lg">Know our customers</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                      <span className="text-gray-300 text-lg">Provide them real time health monitoring</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                      <span className="text-gray-300 text-lg">Predict a coming failure before it occurs</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                      <span className="text-gray-300 text-lg">One tap ordering and delivery (KISS for customer)</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                      <span className="text-gray-300 text-lg">The warranty that works every time for you</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                      <span className="text-gray-300 text-lg">Subscribing to app IS warranty registration</span>
                    </div>
                  </div>
                </div>

                {/* Right Side - iPhone Walkthrough */}
                <div className="col-span-7 flex justify-center">
                  <SmartBatteryWalkthrough />
                </div>

              </div>
            </div>
          </div>
        </div>

      </div>

      {/* Foreground Scenery (Road/Ground) - z-40 to be above red car */}
      <div className="fixed inset-0 pointer-events-none z-20">
        {/* Ground - removed grey border */}
        {/* Road */}
        <div className="absolute bottom-0 w-full h-16 bg-gray-900">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-full h-2 border-t-4 border-dashed border-yellow-400 opacity-60" />
          </div>
        </div>
        
        {/* Road Fork to Corporate Park (only visible on Interstate scene) */}
        <div 
          className="absolute transition-opacity duration-700"
          style={{
            opacity: typeof currentScene !== 'undefined' && currentScene === 3 ? 1 : 0
          }}
        >
          {/* Fork road surface */}
          <div 
            className="absolute bg-gray-900"
            style={{
              bottom: '144px', // Start from main road level (bottom-32 + 24px height)
              left: '60%',
              width: '8px',
              height: '200px',
              transform: 'rotate(-25deg)',
              transformOrigin: 'bottom center'
            }}
          />
          {/* Fork road dashed line */}
          <div 
            className="absolute border-l-2 border-dashed border-yellow-400 opacity-60"
            style={{
              bottom: '144px',
              left: 'calc(60% + 2px)',
              height: '200px',
              transform: 'rotate(-25deg)',
              transformOrigin: 'bottom center'
            }}
          />
        </div>
      </div>

      {/* Animated SVGs (z-30) */}
      <div
        className="fixed inset-0 pointer-events-none"
        style={{ zIndex: topLayer === 'animation' ? 40 : 30 }}
      >
        {/* Continuous Red Car Animation */}
        <div 
          className="absolute bottom-1 transition-all duration-700 pointer-events-auto cursor-pointer"
          onClick={() => setTopLayer('animation')}
          style={{
            left: currentScene === 0 ? '15%' : 
                  currentScene === 1 ? '25%' : 
                  currentScene === 2 ? '35%' : 
                  currentScene === 3 ? '45%' : 
                  currentScene === 5 ? `${45 + (scrollProgress - 0.83) * 200}%` : '-25%',
            opacity: (currentScene === 0 || currentScene === 5) ? 1 : 0,
            transform: `scale(${(currentScene === 0 || currentScene === 5) ? 1 : 0.8})`
          }}
        >
          <div className="relative">
            {/* Car SVG */}
            <svg width="180" height="180" viewBox="0 0 360 360" className="drop-shadow-lg">
              <g>
                <path style={{fill:'rgb(14.901961%,19.607843%,21.960784%)'}} d="M 337.5 240 L 294.75 247.5 L 38.25 247.5 L 30 246 L 16.5 191.25 L 344.25 191.25 Z M 337.5 240"/>
                <path style={{fill:'rgb(37.64706%,49.019608%,54.509807%)'}} d="M 330 243.75 C 330 262.5 315 277.5 296.25 277.5 C 277.5 277.5 262.5 262.5 262.5 243.75 C 262.5 225 277.5 210 296.25 210 C 315 210 330 225 330 243.75 Z M 330 243.75"/>
                <path style={{fill:'rgb(81.176472%,84.705883%,86.274511%)'}} d="M 315 243.75 C 315 254.25 306.75 262.5 296.25 262.5 C 285.75 262.5 277.5 254.25 277.5 243.75 C 277.5 233.25 285.75 225 296.25 225 C 306.75 225 315 233.25 315 243.75 Z M 315 243.75"/>
                <path style={{fill:'rgb(100%,100%,100%)'}} d="M 288.75 247.5 C 286.5 247.5 285 246 285 243.75 C 285 237.75 290.25 232.5 296.25 232.5 C 298.5 232.5 300 234 300 236.25 C 300 238.5 298.5 240 296.25 240 C 294 240 292.5 241.5 292.5 243.75 C 292.5 246 291 247.5 288.75 247.5 Z M 288.75 247.5"/>
                <path style={{fill:'rgb(95.686275%,26.274511%,21.176471%)'}} d="M 37.5 156.75 C 37.5 156.75 55.5 120 62.25 108 C 72 90.75 89.25 75 132 75 C 140.25 75 158.25 75 165 75 C 220.5 75 226.5 90.75 233.25 99.75 C 240 108.75 267 147.75 267 147.75 C 267 147.75 261 162.75 185.25 162.75 C 109.5 162.75 37.5 156.75 37.5 156.75 Z M 37.5 156.75"/>
                <path style={{fill:'rgb(95.686275%,26.274511%,21.176471%)'}} d="M 344.25 175.5 C 344.25 175.5 333.75 144 262.5 142.5 C 260.25 142.5 257.25 143.25 255.75 144 C 247.5 148.5 234 150 228.75 150 L 41.25 150 C 33.75 150 27 153.75 22.5 159 C 13.5 171 0 193.5 0 220.5 C 0 234.75 19.5 242.25 30 245.25 C 30 244.5 30 243.75 30 243 C 30 220.5 48.75 201.75 71.25 201.75 C 93.75 201.75 112.5 220.5 112.5 243 C 112.5 244.5 112.5 245.25 112.5 246.75 L 255 246.75 C 255 245.25 255 244.5 255 243 C 255 220.5 273.75 201.75 296.25 201.75 C 318 201.75 335.25 218.25 337.5 239.25 C 345.75 238.5 352.5 231.75 352.5 223.5 L 352.5 186 C 352.5 177.75 344.25 175.5 344.25 175.5 Z M 344.25 175.5"/>
                <path style={{fill:'rgb(94.901961%,43.529412%,42.352942%)'}} d="M 71.25 195 C 44.25 195 23.25 216.75 22.5 243 C 25.5 243.75 27.75 245.25 30 245.25 C 30 244.5 30 243.75 30 243 C 30 220.5 48.75 201.75 71.25 201.75 C 93.75 201.75 112.5 220.5 112.5 243 C 112.5 244.5 112.5 245.25 112.5 246.75 L 120 246.75 C 120 245.25 120 244.5 120 243 C 120 216.75 98.25 195 71.25 195 Z M 296.25 195 C 269.25 195 247.5 216.75 247.5 243.75 C 247.5 245.25 247.5 246 247.5 247.5 L 255 247.5 C 255 246 255 245.25 255 243.75 C 255 221.25 273.75 202.5 296.25 202.5 C 298.5 202.5 301.5 202.5 303.75 203.25 L 304.5 203.25 C 306.75 204 309 204.75 311.25 205.5 L 312 205.5 C 314.25 206.25 315.75 207 318 208.5 L 318.75 209.25 C 320.25 210.75 322.5 211.5 324 213 L 324.75 213.75 C 326.25 215.25 327.75 216.75 329.25 218.25 L 330 219 C 331.5 220.5 332.25 222.75 333.75 224.25 L 333.75 225 C 334.5 227.25 336 229.5 336 231.75 C 336 231.75 336 231.75 336 232.5 C 336.75 234.75 337.5 237 337.5 240 C 339.75 240 342.75 239.25 344.25 237.75 C 342 213.75 321 195 296.25 195 Z M 296.25 195"/>
                <path style={{fill:'rgb(37.64706%,49.019608%,54.509807%)'}} d="M 105 243.75 C 105 262.5 90 277.5 71.25 277.5 C 52.5 277.5 37.5 262.5 37.5 243.75 C 37.5 225 52.5 210 71.25 210 C 90 210 105 225 105 243.75 Z M 105 243.75"/>
                <path style={{fill:'rgb(81.176472%,84.705883%,86.274511%)'}} d="M 90 243.75 C 90 254.25 81.75 262.5 71.25 262.5 C 60.75 262.5 52.5 254.25 52.5 243.75 C 52.5 233.25 60.75 225 71.25 225 C 81.75 225 90 233.25 90 243.75 Z M 90 243.75"/>
                <path style={{fill:'rgb(100%,100%,100%)'}} d="M 63.75 247.5 C 61.5 247.5 60 246 60 243.75 C 60 237.75 65.25 232.5 71.25 232.5 C 73.5 232.5 75 234 75 236.25 C 75 238.5 73.5 240 71.25 240 C 69 240 67.5 241.5 67.5 243.75 C 67.5 246 66 247.5 63.75 247.5 Z M 63.75 247.5"/>
                <path style={{fill:'rgb(93.725491%,60.392159%,60.392159%)'}} d="M 172.5 180 L 150 180 C 145.5 180 142.5 177 142.5 172.5 C 142.5 168 145.5 165 150 165 L 172.5 165 C 177 165 180 168 180 172.5 C 180 177 177 180 172.5 180 Z M 172.5 180"/>
                <path style={{fill:'rgb(14.901961%,19.607843%,21.960784%)'}} d="M 142.5 150 L 236.25 150 C 242.25 150 246 143.25 242.25 138 L 220.5 105 C 214.5 96 204 90 192.75 90 L 150 90 C 145.5 90 142.5 93 142.5 97.5 Z M 127.5 98.25 L 127.5 142.5 C 127.5 147 124.5 150 120 150 L 66.75 150 C 61.5 150 57.75 144.75 60 139.5 C 66 123.75 82.5 93.75 119.25 90 C 123.75 90 127.5 93.75 127.5 98.25 Z M 127.5 98.25"/>
                <path style={{fill:'rgb(81.176472%,84.705883%,86.274511%)'}} d="M 349.5 232.5 L 355.5 232.5 C 357.75 232.5 360 230.25 360 228 L 360 222 C 360 219.75 357.75 217.5 355.5 217.5 L 349.5 217.5 C 347.25 217.5 345 219.75 345 222 L 345 228 C 345 230.25 347.25 232.5 349.5 232.5 Z M 349.5 232.5"/>
                <path style={{fill:'rgb(100%,80.392158%,82.352942%)'}} d="M 9.75 207.75 L 14.25 189 L 6.75 186 C 4.5 192 2.25 198 1.5 204.75 Z M 9.75 207.75"/>
                <path style={{fill:'rgb(81.176472%,84.705883%,86.274511%)'}} d="M 13.5 222 L 2.25 218.25 C 0.75 217.5 0 216.75 0 215.25 L 0 207 C 0 204.75 2.25 203.25 4.5 204 L 18 208.5 C 20.25 209.25 22.5 212.25 22.5 215.25 C 22.5 219.75 18 223.5 13.5 222 Z M 13.5 222"/>
                <path style={{fill:'rgb(100%,75.686276%,2.745098%)'}} d="M 356.25 210 L 352.5 210 C 352.5 210 348.75 207.75 348.75 198.75 C 348.75 189.75 352.5 187.5 352.5 187.5 L 356.25 187.5 C 358.5 187.5 360 189 360 191.25 L 360 206.25 C 360 208.5 358.5 210 356.25 210 Z M 356.25 210"/>
              </g>
              {/* Animated wheels */}
              <g className="animate-spin origin-center" style={{transformOrigin: '71.25px 243.75px'}}>
                <circle cx="71.25" cy="243.75" r="15" fill="none"/>
              </g>
              <g className="animate-spin origin-center" style={{transformOrigin: '296.25px 243.75px'}}>
                <circle cx="296.25" cy="243.75" r="15" fill="none"/>
              </g>
            </svg>
            {/* Sparkles - only show on final Drive scene */}
            {currentScene === 5 && (
              <>
                <div className="absolute -top-8 left-1/2 transform -translate-x-1/2">
                  <span className="text-yellow-400 text-2xl animate-pulse">✨</span>
                </div>
                <div className="absolute -top-4 left-8">
                  <span className="text-yellow-300 text-lg animate-pulse animation-delay-200">✨</span>
                </div>
                <div className="absolute -top-6 right-8">
                  <span className="text-yellow-300 text-xl animate-pulse animation-delay-400">✨</span>
                </div>
              </>
            )}
            {/* Speed Lines - only show on final Drive scene */}
            {currentScene === 5 && (
              <div className="absolute top-1/2 -left-8 transform -translate-y-1/2">
                <div className="w-16 h-0.5 bg-gray-600 mb-2" />
                <div className="w-12 h-0.5 bg-gray-600 mb-2" />
                <div className="w-20 h-0.5 bg-gray-600" />
              </div>
            )}
          </div>
        </div>

        {/* Scene 2: Breakdown Car (on road) */}
        <div 
          className="absolute bottom-1 transition-all duration-700 pointer-events-auto cursor-pointer"
          onClick={() => setTopLayer('animation')}
          style={{
            left: currentScene === 1 ? '25%' : '15%',
            opacity: currentScene === 1 ? 1 : 0,
            transform: `scale(${currentScene === 1 ? 1 : 0.8})`
          }}
        >
          <div className="relative">
            {/* Red Broken Car */}
            <CarBreakdown width="180" height="180" className="drop-shadow-lg" />
          </div>
        </div>

        {/* Scene 3: Pickup */}
        <div 
          className="absolute bottom-1 transition-all duration-700 pointer-events-auto cursor-pointer"
          onClick={() => setTopLayer('animation')}
          style={{
            left: currentScene === 2 ? '35%' : '25%',
            opacity: currentScene === 2 ? 1 : 0,
            transform: `scale(${currentScene === 2 ? 1 : 0.8})`
          }}
        >
          <div className="flex items-end">
            {/* Towed Car - positioned to the left and angled with hood up, trunk down */}
            <div className="relative transform -rotate-12 translate-y-2">
              <svg width="180" height="180" viewBox="0 0 360 360" className="drop-shadow-lg">
                <g>
                  <path style={{fill:'rgb(14.901961%,19.607843%,21.960784%)'}} d="M 337.5 240 L 294.75 247.5 L 38.25 247.5 L 30 246 L 16.5 191.25 L 344.25 191.25 Z M 337.5 240"/>
                  <path style={{fill:'rgb(37.64706%,49.019608%,54.509807%)'}} d="M 330 243.75 C 330 262.5 315 277.5 296.25 277.5 C 277.5 277.5 262.5 262.5 262.5 243.75 C 262.5 225 277.5 210 296.25 210 C 315 210 330 225 330 243.75 Z M 330 243.75"/>
                  <path style={{fill:'rgb(81.176472%,84.705883%,86.274511%)'}} d="M 315 243.75 C 315 254.25 306.75 262.5 296.25 262.5 C 285.75 262.5 277.5 254.25 277.5 243.75 C 277.5 233.25 285.75 225 296.25 225 C 306.75 225 315 233.25 315 243.75 Z M 315 243.75"/>
                  <path style={{fill:'rgb(100%,100%,100%)'}} d="M 288.75 247.5 C 286.5 247.5 285 246 285 243.75 C 285 237.75 290.25 232.5 296.25 232.5 C 298.5 232.5 300 234 300 236.25 C 300 238.5 298.5 240 296.25 240 C 294 240 292.5 241.5 292.5 243.75 C 292.5 246 291 247.5 288.75 247.5 Z M 288.75 247.5"/>
                  <path style={{fill:'rgb(95.686275%,26.274511%,21.176471%)'}} d="M 37.5 156.75 C 37.5 156.75 55.5 120 62.25 108 C 72 90.75 89.25 75 132 75 C 140.25 75 158.25 75 165 75 C 220.5 75 226.5 90.75 233.25 99.75 C 240 108.75 267 147.75 267 147.75 C 267 147.75 261 162.75 185.25 162.75 C 109.5 162.75 37.5 156.75 37.5 156.75 Z M 37.5 156.75"/>
                  <path style={{fill:'rgb(95.686275%,26.274511%,21.176471%)'}} d="M 344.25 175.5 C 344.25 175.5 333.75 144 262.5 142.5 C 260.25 142.5 257.25 143.25 255.75 144 C 247.5 148.5 234 150 228.75 150 L 41.25 150 C 33.75 150 27 153.75 22.5 159 C 13.5 171 0 193.5 0 220.5 C 0 234.75 19.5 242.25 30 245.25 C 30 244.5 30 243.75 30 243 C 30 220.5 48.75 201.75 71.25 201.75 C 93.75 201.75 112.5 220.5 112.5 243 C 112.5 244.5 112.5 245.25 112.5 246.75 L 255 246.75 C 255 245.25 255 244.5 255 243 C 255 220.5 273.75 201.75 296.25 201.75 C 318 201.75 335.25 218.25 337.5 239.25 C 345.75 238.5 352.5 231.75 352.5 223.5 L 352.5 186 C 352.5 177.75 344.25 175.5 344.25 175.5 Z M 344.25 175.5"/>
                  <path style={{fill:'rgb(94.901961%,43.529412%,42.352942%)'}} d="M 71.25 195 C 44.25 195 23.25 216.75 22.5 243 C 25.5 243.75 27.75 245.25 30 245.25 C 30 244.5 30 243.75 30 243 C 30 220.5 48.75 201.75 71.25 201.75 C 93.75 201.75 112.5 220.5 112.5 243 C 112.5 244.5 112.5 245.25 112.5 246.75 L 120 246.75 C 120 245.25 120 244.5 120 243 C 120 216.75 98.25 195 71.25 195 Z M 296.25 195 C 269.25 195 247.5 216.75 247.5 243.75 C 247.5 245.25 247.5 246 247.5 247.5 L 255 247.5 C 255 246 255 245.25 255 243.75 C 255 221.25 273.75 202.5 296.25 202.5 C 298.5 202.5 301.5 202.5 303.75 203.25 L 304.5 203.25 C 306.75 204 309 204.75 311.25 205.5 L 312 205.5 C 314.25 206.25 315.75 207 318 208.5 L 318.75 209.25 C 320.25 210.75 322.5 211.5 324 213 L 324.75 213.75 C 326.25 215.25 327.75 216.75 329.25 218.25 L 330 219 C 331.5 220.5 332.25 222.75 333.75 224.25 L 333.75 225 C 334.5 227.25 336 229.5 336 231.75 C 336 231.75 336 231.75 336 232.5 C 336.75 234.75 337.5 237 337.5 240 C 339.75 240 342.75 239.25 344.25 237.75 C 342 213.75 321 195 296.25 195 Z M 296.25 195"/>
                  <path style={{fill:'rgb(37.64706%,49.019608%,54.509807%)'}} d="M 105 243.75 C 105 262.5 90 277.5 71.25 277.5 C 52.5 277.5 37.5 262.5 37.5 243.75 C 37.5 225 52.5 210 71.25 210 C 90 210 105 225 105 243.75 Z M 105 243.75"/>
                  <path style={{fill:'rgb(81.176472%,84.705883%,86.274511%)'}} d="M 90 243.75 C 90 254.25 81.75 262.5 71.25 262.5 C 60.75 262.5 52.5 254.25 52.5 243.75 C 52.5 233.25 60.75 225 71.25 225 C 81.75 225 90 233.25 90 243.75 Z M 90 243.75"/>
                  <path style={{fill:'rgb(100%,100%,100%)'}} d="M 63.75 247.5 C 61.5 247.5 60 246 60 243.75 C 60 237.75 65.25 232.5 71.25 232.5 C 73.5 232.5 75 234 75 236.25 C 75 238.5 73.5 240 71.25 240 C 69 240 67.5 241.5 67.5 243.75 C 67.5 246 66 247.5 63.75 247.5 Z M 63.75 247.5"/>
                  <path style={{fill:'rgb(93.725491%,60.392159%,60.392159%)'}} d="M 172.5 180 L 150 180 C 145.5 180 142.5 177 142.5 172.5 C 142.5 168 145.5 165 150 165 L 172.5 165 C 177 165 180 168 180 172.5 C 180 177 177 180 172.5 180 Z M 172.5 180"/>
                  <path style={{fill:'rgb(14.901961%,19.607843%,21.960784%)'}} d="M 142.5 150 L 236.25 150 C 242.25 150 246 143.25 242.25 138 L 220.5 105 C 214.5 96 204 90 192.75 90 L 150 90 C 145.5 90 142.5 93 142.5 97.5 Z M 127.5 98.25 L 127.5 142.5 C 127.5 147 124.5 150 120 150 L 66.75 150 C 61.5 150 57.75 144.75 60 139.5 C 66 123.75 82.5 93.75 119.25 90 C 123.75 90 127.5 93.75 127.5 98.25 Z M 127.5 98.25"/>
                  <path style={{fill:'rgb(81.176472%,84.705883%,86.274511%)'}} d="M 349.5 232.5 L 355.5 232.5 C 357.75 232.5 360 230.25 360 228 L 360 222 C 360 219.75 357.75 217.5 355.5 217.5 L 349.5 217.5 C 347.25 217.5 345 219.75 345 222 L 345 228 C 345 230.25 347.25 232.5 349.5 232.5 Z M 349.5 232.5"/>
                  <path style={{fill:'rgb(100%,80.392158%,82.352942%)'}} d="M 9.75 207.75 L 14.25 189 L 6.75 186 C 4.5 192 2.25 198 1.5 204.75 Z M 9.75 207.75"/>
                  <path style={{fill:'rgb(81.176472%,84.705883%,86.274511%)'}} d="M 13.5 222 L 2.25 218.25 C 0.75 217.5 0 216.75 0 215.25 L 0 207 C 0 204.75 2.25 203.25 4.5 204 L 18 208.5 C 20.25 209.25 22.5 212.25 22.5 215.25 C 22.5 219.75 18 223.5 13.5 222 Z M 13.5 222"/>
                  <path style={{fill:'rgb(100%,75.686276%,2.745098%)'}} d="M 356.25 210 L 352.5 210 C 352.5 210 348.75 207.75 348.75 198.75 C 348.75 189.75 352.5 187.5 352.5 187.5 L 356.25 187.5 C 358.5 187.5 360 189 360 191.25 L 360 206.25 C 360 208.5 358.5 210 356.25 210 Z M 356.25 210"/>
                </g>
              </svg>
            </div>
            {/* Tow Truck - positioned to the right */}
            <div className="relative translate-x-12">
              <svg width="180" height="180" viewBox="0 0 360 360" className="drop-shadow-lg">
                <g>
                  <path style={{ stroke:"none", fillRule:"nonzero", fill:"rgb(100%,62.7451%,0%)", fillOpacity:1 }} d="M 210 255 L 21.75 255 C 21.75 255 7.5 255.75 7.5 240 C 7.5 224.25 7.5 202.5 7.5 202.5 L 210 202.5 Z M 210 255 "/>
                  <path style={{ stroke:"none", fillRule:"nonzero", fill:"rgb(100%,75.686276%,2.745098%)", fillOpacity:1 }} d="M 337.5 270 L 202.5 270 C 202.5 270 202.5 150 202.5 135 C 202.5 120 217.5 120 217.5 120 L 312 120 C 318.75 120 324 124.5 326.25 130.5 L 352.5 195 L 352.5 255 C 352.5 263.25 345.75 270 337.5 270 "/>
                  <path style={{ stroke:"none", fillRule:"nonzero", fill:"rgb(47.843137%,10.196079%,1.568628%)", fillOpacity:1 }} d="M 322.5 195 L 270 195 C 265.5 195 262.5 192 262.5 187.5 L 262.5 142.5 C 262.5 138 265.5 135 270 135 L 307.5 135 C 310.5 135 313.5 137.25 314.25 140.25 L 329.25 177.75 C 329.25 178.5 330 179.25 330 180 L 330 187.5 C 330 192 327 195 322.5 195 "/>
                  <path style={{ stroke:"none", fillRule:"nonzero", fill:"rgb(37.64706%,49.019608%,54.509807%)", fillOpacity:1 }} d="M 52.5 129 L 52.5 67.5 L 37.5 67.5 L 37.5 135 C 37.5 135 37.5 142.5 45 142.5 C 49.5 142.5 52.5 145.5 52.5 150 C 52.5 154.5 49.5 157.5 45 157.5 C 40.5 157.5 37.5 154.5 37.5 150 L 22.5 150 C 22.5 162.75 32.25 172.5 45 172.5 C 57.75 172.5 67.5 162.75 67.5 150 C 67.5 140.25 61.5 132 52.5 129 Z M 52.5 129 "/>
                  <path style={{ stroke:"none", fillRule:"nonzero", fill:"rgb(69.01961%,74.509805%,77.254903%)", fillOpacity:1 }} d="M 165 202.5 C 162.75 202.5 161.25 201.75 159.75 200.25 L 39.75 72.75 C 36.75 69.75 36.75 65.25 39.75 62.25 L 47.25 54.75 C 50.25 51.75 54.75 51.75 57.75 54.75 L 185.25 167.25 C 188.25 169.5 188.25 174 186.75 177 L 171.75 199.5 C 170.25 201 168 202.5 165 202.5 C 165.75 202.5 165 202.5 165 202.5 Z M 83.25 96.75 L 164.25 183 L 170.25 174 Z M 83.25 96.75 "/>
                  <path style={{ stroke:"none", fillRule:"nonzero", fill:"rgb(56.470591%,64.313728%,68.235296%)", fillOpacity:1 }} d="M 285 277.5 L 90 277.5 L 90 247.5 L 285 247.5 Z M 135 202.5 L 187.5 202.5 L 187.5 172.5 L 135 172.5 Z M 135 202.5 "/>
                  <path style={{ stroke:"none", fillRule:"nonzero", fill:"rgb(27.058825%,35.294119%,39.215687%)", fillOpacity:1 }} d="M 120 270 C 120 291 103.5 307.5 82.5 307.5 C 61.5 307.5 45 291 45 270 C 45 249 61.5 232.5 82.5 232.5 C 103.5 232.5 120 249 120 270 M 315 270 C 315 249 298.5 232.5 277.5 232.5 C 256.5 232.5 240 249 240 270 C 240 291 256.5 307.5 277.5 307.5 C 298.5 307.5 315 291 315 270 "/>
                  <path style={{ stroke:"none", fillRule:"nonzero", fill:"rgb(81.176472%,84.705883%,86.274511%)", fillOpacity:1 }} d="M 97.5 270 C 97.5 278.25 90.75 285 82.5 285 C 74.25 285 67.5 278.25 67.5 270 C 67.5 261.75 74.25 255 82.5 255 C 90.75 255 97.5 261.75 97.5 270 M 292.5 270 C 292.5 261.75 285.75 255 277.5 255 C 269.25 255 262.5 261.75 262.5 270 C 262.5 278.25 269.25 285 277.5 285 C 285.75 285 292.5 278.25 292.5 270 "/>
                </g>
              </svg>
            </div>
          </div>
        </div>

        {/* Scene 4: Buy Interstate - Show red broken car in front of company building */}
        <div 
          className="absolute bottom-1 transition-all duration-700 pointer-events-auto cursor-pointer"
          onClick={() => setTopLayer('animation')}
          style={{
            left: currentScene === 3 ? '45%' : '35%',
            opacity: currentScene === 3 ? 1 : 0,
            transform: `scale(${currentScene === 3 ? 1 : 0.8})`
          }}
        >
          <div className="relative flex items-end">
            {/* Company Building in background */}
            <div className="absolute bottom-8 -left-16 z-10">
              <CompanyBuilding width="240" height="240" className="drop-shadow-lg" />
            </div>
            {/* Red Broken Car in foreground */}
            <div className="relative z-20 translate-x-6">
              <GirlAndCar width="180" height="180" className="drop-shadow-lg" />
            </div>
          </div>
        </div>


        {/* Scene 5: Battery Repair Shop */}
        <div 
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2 transition-all duration-700 pointer-events-auto cursor-pointer"
          onClick={() => setTopLayer('animation')}
          style={{
            opacity: currentScene === 4 ? 1 : 0,
            transform: `translateX(-50%) scale(${currentScene === 4 ? 1 : 0.8})`
          }}
        >
          {/* Garage */}
          <div className="relative">
            <div className="w-[400px] h-56 bg-gradient-to-b from-gray-300 to-gray-400 rounded-t-2xl border-4 border-gray-700 shadow-2xl">
              {/* Garage Interior */}
              <div className="absolute inset-2 bg-gradient-to-b from-gray-200 to-gray-300 rounded-t-xl overflow-hidden">
                {/* Back Wall */}
                <div className="absolute inset-x-0 top-0 h-24 bg-gray-400">
                  {/* Tool Board */}
                  <div className="absolute left-4 top-2 w-16 h-16 bg-amber-800 rounded shadow-md">
                    <div className="grid grid-cols-3 gap-1 p-1">
                      <div className="w-3 h-3 bg-gray-600 rounded-full" />
                      <div className="w-3 h-1 bg-gray-700" />
                      <div className="w-2 h-2 bg-red-600 transform rotate-45" />
                      <div className="w-3 h-3 bg-gray-500" />
                      <div className="w-3 h-1 bg-gray-600 rounded" />
                      <div className="w-2 h-2 bg-blue-600" />
                    </div>
                  </div>
                  {/* Shop Sign */}
                  <div className="absolute top-2 left-1/2 transform -translate-x-1/2 bg-blue-600 text-white px-3 py-1 rounded text-xs font-bold">
                    Mechanic
                  </div>
                </div>
                
                {/* Car on simple platform */}
                <div className="absolute bottom-16 left-1/2 transform -translate-x-1/2">
                  <svg width="120" height="120" viewBox="0 0 360 360" className="drop-shadow-lg">
                    <g>
                      <path style={{fill:'rgb(81.176472%,84.705883%,86.274511%)'}} d="M 90 243.75 C 90 254.25 81.75 262.5 71.25 262.5 C 60.75 262.5 52.5 254.25 52.5 243.75 C 52.5 233.25 60.75 225 71.25 225 C 81.75 225 90 233.25 90 243.75 Z M 90 243.75"/>
                      <path style={{fill:'rgb(100%,100%,100%)'}} d="M 63.75 247.5 C 61.5 247.5 60 246 60 243.75 C 60 237.75 65.25 232.5 71.25 232.5 C 73.5 232.5 75 234 75 236.25 C 75 238.5 73.5 240 71.25 240 C 69 240 67.5 241.5 67.5 243.75 C 67.5 246 66 247.5 63.75 247.5 Z M 63.75 247.5"/>
                      <path style={{fill:'rgb(37.64706%,49.019608%,54.509807%)'}} d="M 330 243.75 C 330 262.5 315 277.5 296.25 277.5 C 277.5 277.5 262.5 262.5 262.5 243.75 C 262.5 225 277.5 210 296.25 210 C 315 210 330 225 330 243.75 Z M 330 243.75"/>
                      <path style={{fill:'rgb(81.176472%,84.705883%,86.274511%)'}} d="M 315 243.75 C 315 254.25 306.75 262.5 296.25 262.5 C 285.75 262.5 277.5 254.25 277.5 243.75 C 277.5 233.25 285.75 225 296.25 225 C 306.75 225 315 233.25 315 243.75 Z M 315 243.75"/>
                      <path style={{fill:'rgb(100%,100%,100%)'}} d="M 288.75 247.5 C 286.5 247.5 285 246 285 243.75 C 285 237.75 290.25 232.5 296.25 232.5 C 298.5 232.5 300 234 300 236.25 C 300 238.5 298.5 240 296.25 240 C 294 240 292.5 241.5 292.5 243.75 C 292.5 246 291 247.5 288.75 247.5 Z M 288.75 247.5"/>
                      <path style={{fill:'rgb(95.686275%,26.274511%,21.176471%)'}} d="M 37.5 156.75 C 37.5 156.75 55.5 120 62.25 108 C 72 90.75 89.25 75 132 75 C 140.25 75 158.25 75 165 75 C 220.5 75 226.5 90.75 233.25 99.75 C 240 108.75 267 147.75 267 147.75 C 267 147.75 261 162.75 185.25 162.75 C 109.5 162.75 37.5 156.75 37.5 156.75 Z M 37.5 156.75"/>
                      <path style={{fill:'rgb(95.686275%,26.274511%,21.176471%)'}} d="M 344.25 175.5 C 344.25 175.5 333.75 144 262.5 142.5 C 260.25 142.5 257.25 143.25 255.75 144 C 247.5 148.5 234 150 228.75 150 L 41.25 150 C 33.75 150 27 153.75 22.5 159 C 13.5 171 0 193.5 0 220.5 C 0 234.75 19.5 242.25 30 245.25 C 30 244.5 30 243.75 30 243 C 30 220.5 48.75 201.75 71.25 201.75 C 93.75 201.75 112.5 220.5 112.5 243 C 112.5 244.5 112.5 245.25 112.5 246.75 L 255 246.75 C 255 245.25 255 244.5 255 243 C 255 220.5 273.75 201.75 296.25 201.75 C 318 201.75 335.25 218.25 337.5 239.25 C 345.75 238.5 352.5 231.75 352.5 223.5 L 352.5 186 C 352.5 177.75 344.25 175.5 344.25 175.5 Z M 344.25 175.5"/>
                      <path style={{fill:'rgb(94.901961%,43.529412%,42.352942%)'}} d="M 71.25 195 C 44.25 195 23.25 216.75 22.5 243 C 25.5 243.75 27.75 245.25 30 245.25 C 30 244.5 30 243.75 30 243 C 30 220.5 48.75 201.75 71.25 201.75 C 93.75 201.75 112.5 220.5 112.5 243 C 112.5 244.5 112.5 245.25 112.5 246.75 L 120 246.75 C 120 245.25 120 244.5 120 243 C 120 216.75 98.25 195 71.25 195 Z M 296.25 195 C 269.25 195 247.5 216.75 247.5 243.75 C 247.5 245.25 247.5 246 247.5 247.5 L 255 247.5 C 255 246 255 245.25 255 243.75 C 255 221.25 273.75 202.5 296.25 202.5 C 298.5 202.5 301.5 202.5 303.75 203.25 L 304.5 203.25 C 306.75 204 309 204.75 311.25 205.5 L 312 205.5 C 314.25 206.25 315.75 207 318 208.5 L 318.75 209.25 C 320.25 210.75 322.5 211.5 324 213 L 324.75 213.75 C 326.25 215.25 327.75 216.75 329.25 218.25 L 330 219 C 331.5 220.5 332.25 222.75 333.75 224.25 L 333.75 225 C 334.5 227.25 336 229.5 336 231.75 C 336 231.75 336 231.75 336 232.5 C 336.75 234.75 337.5 237 337.5 240 C 339.75 240 342.75 239.25 344.25 237.75 C 342 213.75 321 195 296.25 195 Z M 296.25 195"/>
                      <path style={{fill:'rgb(37.64706%,49.019608%,54.509807%)'}} d="M 105 243.75 C 105 262.5 90 277.5 71.25 277.5 C 52.5 277.5 37.5 262.5 37.5 243.75 C 37.5 225 52.5 210 71.25 210 C 90 210 105 225 105 243.75 Z M 105 243.75"/>
                      <path style={{fill:'rgb(93.725491%,60.392159%,60.392159%)'}} d="M 172.5 180 L 150 180 C 145.5 180 142.5 177 142.5 172.5 C 142.5 168 145.5 165 150 165 L 172.5 165 C 177 165 180 168 180 172.5 C 180 177 177 180 172.5 180 Z M 172.5 180"/>
                      <path style={{fill:'rgb(14.901961%,19.607843%,21.960784%)'}} d="M 142.5 150 L 236.25 150 C 242.25 150 246 143.25 242.25 138 L 220.5 105 C 214.5 96 204 90 192.75 90 L 150 90 C 145.5 90 142.5 93 142.5 97.5 Z M 127.5 98.25 L 127.5 142.5 C 127.5 147 124.5 150 120 150 L 66.75 150 C 61.5 150 57.75 144.75 60 139.5 C 66 123.75 82.5 93.75 119.25 90 C 123.75 90 127.5 93.75 127.5 98.25 Z M 127.5 98.25"/>
                      <path style={{fill:'rgb(81.176472%,84.705883%,86.274511%)'}} d="M 349.5 232.5 L 355.5 232.5 C 357.75 232.5 360 230.25 360 228 L 360 222 C 360 219.75 357.75 217.5 355.5 217.5 L 349.5 217.5 C 347.25 217.5 345 219.75 345 222 L 345 228 C 345 230.25 347.25 232.5 349.5 232.5 Z M 349.5 232.5"/>
                      <path style={{fill:'rgb(100%,80.392158%,82.352942%)'}} d="M 9.75 207.75 L 14.25 189 L 6.75 186 C 4.5 192 2.25 198 1.5 204.75 Z M 9.75 207.75"/>
                      <path style={{fill:'rgb(81.176472%,84.705883%,86.274511%)'}} d="M 13.5 222 L 2.25 218.25 C 0.75 217.5 0 216.75 0 215.25 L 0 207 C 0 204.75 2.25 203.25 4.5 204 L 18 208.5 C 20.25 209.25 22.5 212.25 22.5 215.25 C 22.5 219.75 18 223.5 13.5 222 Z M 13.5 222"/>
                      <path style={{fill:'rgb(100%,75.686276%,2.745098%)'}} d="M 356.25 210 L 352.5 210 C 352.5 210 348.75 207.75 348.75 198.75 C 348.75 189.75 352.5 187.5 352.5 187.5 L 356.25 187.5 C 358.5 187.5 360 189 360 191.25 L 360 206.25 C 360 208.5 358.5 210 356.25 210 Z M 356.25 210"/>
                    </g>
                  </svg>
                  {/* Simple Lift Platform */}
                  <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 w-32 h-4 bg-yellow-600 rounded shadow-md">
                    <div className="absolute inset-x-4 top-1 h-1 bg-yellow-700" />
                  </div>
                </div>
                
                {/* Mechanic */}
                <div className="absolute bottom-1 left-6">
                  {/* Head */}
                  <div className="w-8 h-8 bg-pink-300 rounded-full relative">
                    {/* Cap */}
                    <div className="absolute -top-1 left-0 right-0 h-4 bg-blue-800 rounded-t-full">
                      <div className="absolute bottom-0 left-0 right-0 h-1 bg-blue-900" />
                    </div>
                    {/* Eyes */}
                    <div className="absolute top-2 left-1 w-1 h-1 bg-black rounded-full" />
                    <div className="absolute top-2 right-1 w-1 h-1 bg-black rounded-full" />
                  </div>
                  {/* Body */}
                  <div className="w-10 h-12 bg-blue-700 rounded-t-lg mt-1 relative">
                    <div className="absolute top-1 left-1/2 transform -translate-x-1/2 w-8 h-10 bg-blue-600 rounded">
                      {/* Name tag */}
                      <div className="absolute top-1 right-1 w-3 h-2 bg-white rounded text-xs flex items-center justify-center">
                        <span className="text-black font-bold text-[6px]">JOE</span>
                      </div>
                    </div>
                  </div>
                  {/* Legs */}
                  <div className="flex gap-1">
                    <div className="w-4 h-8 bg-blue-900 rounded-b" />
                    <div className="w-4 h-8 bg-blue-900 rounded-b" />
                  </div>
                </div>
                
                {/* New Battery Display */}
                <div className="absolute bottom-4 right-6 bg-white/95 p-2 rounded-lg shadow-lg border border-gray-300">
                  <svg width="40" height="40" viewBox="0 0 360 360" xmlns="http://www.w3.org/2000/svg">
                    {/* Battery Body - Dark Gray */}
                    <rect x="45" y="135" width="270" height="180" fill="#455A64"/>
                    {/* Battery Top - Light Gray */}
                    <rect x="45" y="105" width="270" height="30" fill="#B0BEC5"/>
                    {/* Positive Terminal - Orange/Red */}
                    <rect x="240" y="45" width="37.5" height="60" rx="3" fill="#FF6100"/>
                    {/* Positive Terminal Band - Dark Red */}
                    <rect x="225" y="90" width="67.5" height="15" fill="#D32F2F"/>
                    {/* Negative Terminal - Blue */}
                    <rect x="82.5" y="45" width="37.5" height="60" rx="3" fill="#2196F3"/>
                    {/* Negative Terminal Band - Dark Blue */}
                    <rect x="67.5" y="90" width="67.5" height="15" fill="#1565C0"/>
                    {/* Plus Symbol - Vertical */}
                    <rect x="240" y="187.5" width="22.5" height="67.5" fill="#ECEFF1"/>
                    {/* Plus Symbol - Horizontal */}
                    <rect x="217.5" y="210" width="67.5" height="22.5" fill="#ECEFF1"/>
                    {/* Minus Symbol */}
                    <rect x="75" y="210" width="67.5" height="22.5" fill="#ECEFF1"/>
                  </svg>
                  {/* Lightning bolt */}
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 text-yellow-400 text-lg animate-pulse">⚡</div>
                  {/* Label */}
                  <div className="text-center mt-1 text-gray-700 font-bold text-[10px]">NEW BATTERY</div>
                </div>
                
                {/* Garage Floor */}
                <div className="absolute bottom-0 left-0 right-0 h-4 bg-gray-500 border-t-2 border-gray-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Removed - using continuous red car animation instead */}
        <div style={{display: 'none'}}>
          <div className="relative">
            {/* Happy Car SVG */}
            <svg width="180" height="180" viewBox="0 0 360 360" className="drop-shadow-lg">
              <g>
                <path style={{fill:'rgb(14.901961%,19.607843%,21.960784%)'}} d="M 337.5 240 L 294.75 247.5 L 38.25 247.5 L 30 246 L 16.5 191.25 L 344.25 191.25 Z M 337.5 240"/>
                <path style={{fill:'rgb(37.64706%,49.019608%,54.509807%)'}} d="M 330 243.75 C 330 262.5 315 277.5 296.25 277.5 C 277.5 277.5 262.5 262.5 262.5 243.75 C 262.5 225 277.5 210 296.25 210 C 315 210 330 225 330 243.75 Z M 330 243.75"/>
                <path style={{fill:'rgb(81.176472%,84.705883%,86.274511%)'}} d="M 315 243.75 C 315 254.25 306.75 262.5 296.25 262.5 C 285.75 262.5 277.5 254.25 277.5 243.75 C 277.5 233.25 285.75 225 296.25 225 C 306.75 225 315 233.25 315 243.75 Z M 315 243.75"/>
                <path style={{fill:'rgb(100%,100%,100%)'}} d="M 288.75 247.5 C 286.5 247.5 285 246 285 243.75 C 285 237.75 290.25 232.5 296.25 232.5 C 298.5 232.5 300 234 300 236.25 C 300 238.5 298.5 240 296.25 240 C 294 240 292.5 241.5 292.5 243.75 C 292.5 246 291 247.5 288.75 247.5 Z M 288.75 247.5"/>
                <path style={{fill:'rgb(95.686275%,26.274511%,21.176471%)'}} d="M 37.5 156.75 C 37.5 156.75 55.5 120 62.25 108 C 72 90.75 89.25 75 132 75 C 140.25 75 158.25 75 165 75 C 220.5 75 226.5 90.75 233.25 99.75 C 240 108.75 267 147.75 267 147.75 C 267 147.75 261 162.75 185.25 162.75 C 109.5 162.75 37.5 156.75 37.5 156.75 Z M 37.5 156.75"/>
                <path style={{fill:'rgb(95.686275%,26.274511%,21.176471%)'}} d="M 344.25 175.5 C 344.25 175.5 333.75 144 262.5 142.5 C 260.25 142.5 257.25 143.25 255.75 144 C 247.5 148.5 234 150 228.75 150 L 41.25 150 C 33.75 150 27 153.75 22.5 159 C 13.5 171 0 193.5 0 220.5 C 0 234.75 19.5 242.25 30 245.25 C 30 244.5 30 243.75 30 243 C 30 220.5 48.75 201.75 71.25 201.75 C 93.75 201.75 112.5 220.5 112.5 243 C 112.5 244.5 112.5 245.25 112.5 246.75 L 255 246.75 C 255 245.25 255 244.5 255 243 C 255 220.5 273.75 201.75 296.25 201.75 C 318 201.75 335.25 218.25 337.5 239.25 C 345.75 238.5 352.5 231.75 352.5 223.5 L 352.5 186 C 352.5 177.75 344.25 175.5 344.25 175.5 Z M 344.25 175.5"/>
                <path style={{fill:'rgb(94.901961%,43.529412%,42.352942%)'}} d="M 71.25 195 C 44.25 195 23.25 216.75 22.5 243 C 25.5 243.75 27.75 245.25 30 245.25 C 30 244.5 30 243.75 30 243 C 30 220.5 48.75 201.75 71.25 201.75 C 93.75 201.75 112.5 220.5 112.5 243 C 112.5 244.5 112.5 245.25 112.5 246.75 L 120 246.75 C 120 245.25 120 244.5 120 243 C 120 216.75 98.25 195 71.25 195 Z M 296.25 195 C 269.25 195 247.5 216.75 247.5 243.75 C 247.5 245.25 247.5 246 247.5 247.5 L 255 247.5 C 255 246 255 245.25 255 243.75 C 255 221.25 273.75 202.5 296.25 202.5 C 298.5 202.5 301.5 202.5 303.75 203.25 L 304.5 203.25 C 306.75 204 309 204.75 311.25 205.5 L 312 205.5 C 314.25 206.25 315.75 207 318 208.5 L 318.75 209.25 C 320.25 210.75 322.5 211.5 324 213 L 324.75 213.75 C 326.25 215.25 327.75 216.75 329.25 218.25 L 330 219 C 331.5 220.5 332.25 222.75 333.75 224.25 L 333.75 225 C 334.5 227.25 336 229.5 336 231.75 C 336 231.75 336 231.75 336 232.5 C 336.75 234.75 337.5 237 337.5 240 C 339.75 240 342.75 239.25 344.25 237.75 C 342 213.75 321 195 296.25 195 Z M 296.25 195"/>
                <path style={{fill:'rgb(37.64706%,49.019608%,54.509807%)'}} d="M 105 243.75 C 105 262.5 90 277.5 71.25 277.5 C 52.5 277.5 37.5 262.5 37.5 243.75 C 37.5 225 52.5 210 71.25 210 C 90 210 105 225 105 243.75 Z M 105 243.75"/>
                <path style={{fill:'rgb(81.176472%,84.705883%,86.274511%)'}} d="M 90 243.75 C 90 254.25 81.75 262.5 71.25 262.5 C 60.75 262.5 52.5 254.25 52.5 243.75 C 52.5 233.25 60.75 225 71.25 225 C 81.75 225 90 233.25 90 243.75 Z M 90 243.75"/>
                <path style={{fill:'rgb(100%,100%,100%)'}} d="M 63.75 247.5 C 61.5 247.5 60 246 60 243.75 C 60 237.75 65.25 232.5 71.25 232.5 C 73.5 232.5 75 234 75 236.25 C 75 238.5 73.5 240 71.25 240 C 69 240 67.5 241.5 67.5 243.75 C 67.5 246 66 247.5 63.75 247.5 Z M 63.75 247.5"/>
                <path style={{fill:'rgb(93.725491%,60.392159%,60.392159%)'}} d="M 172.5 180 L 150 180 C 145.5 180 142.5 177 142.5 172.5 C 142.5 168 145.5 165 150 165 L 172.5 165 C 177 165 180 168 180 172.5 C 180 177 177 180 172.5 180 Z M 172.5 180"/>
                <path style={{fill:'rgb(14.901961%,19.607843%,21.960784%)'}} d="M 142.5 150 L 236.25 150 C 242.25 150 246 143.25 242.25 138 L 220.5 105 C 214.5 96 204 90 192.75 90 L 150 90 C 145.5 90 142.5 93 142.5 97.5 Z M 127.5 98.25 L 127.5 142.5 C 127.5 147 124.5 150 120 150 L 66.75 150 C 61.5 150 57.75 144.75 60 139.5 C 66 123.75 82.5 93.75 119.25 90 C 123.75 90 127.5 93.75 127.5 98.25 Z M 127.5 98.25"/>
                <path style={{fill:'rgb(81.176472%,84.705883%,86.274511%)'}} d="M 349.5 232.5 L 355.5 232.5 C 357.75 232.5 360 230.25 360 228 L 360 222 C 360 219.75 357.75 217.5 355.5 217.5 L 349.5 217.5 C 347.25 217.5 345 219.75 345 222 L 345 228 C 345 230.25 347.25 232.5 349.5 232.5 Z M 349.5 232.5"/>
                <path style={{fill:'rgb(100%,80.392158%,82.352942%)'}} d="M 9.75 207.75 L 14.25 189 L 6.75 186 C 4.5 192 2.25 198 1.5 204.75 Z M 9.75 207.75"/>
                <path style={{fill:'rgb(81.176472%,84.705883%,86.274511%)'}} d="M 13.5 222 L 2.25 218.25 C 0.75 217.5 0 216.75 0 215.25 L 0 207 C 0 204.75 2.25 203.25 4.5 204 L 18 208.5 C 20.25 209.25 22.5 212.25 22.5 215.25 C 22.5 219.75 18 223.5 13.5 222 Z M 13.5 222"/>
                <path style={{fill:'rgb(100%,75.686276%,2.745098%)'}} d="M 356.25 210 L 352.5 210 C 352.5 210 348.75 207.75 348.75 198.75 C 348.75 189.75 352.5 187.5 352.5 187.5 L 356.25 187.5 C 358.5 187.5 360 189 360 191.25 L 360 206.25 C 360 208.5 358.5 210 356.25 210 Z M 356.25 210"/>
              </g>
              {/* Animated wheels */}
              <g className="animate-spin origin-center" style={{transformOrigin: '71.25px 243.75px'}}>
                <circle cx="71.25" cy="243.75" r="15" fill="none"/>
              </g>
              <g className="animate-spin origin-center" style={{transformOrigin: '296.25px 243.75px'}}>
                <circle cx="296.25" cy="243.75" r="15" fill="none"/>
              </g>
            </svg>
            {/* Sparkles */}
            <div className="absolute -top-8 left-1/2 transform -translate-x-1/2">
              <span className="text-yellow-400 text-2xl animate-pulse">✨</span>
            </div>
            <div className="absolute -top-4 left-8">
              <span className="text-yellow-300 text-lg animate-pulse animation-delay-200">✨</span>
            </div>
            <div className="absolute -top-6 right-8">
              <span className="text-yellow-300 text-xl animate-pulse animation-delay-400">✨</span>
            </div>
            {/* Speed Lines */}
            <div className="absolute top-1/2 -left-8 transform -translate-y-1/2">
              <div className="w-16 h-0.5 bg-gray-600 mb-2" />
              <div className="w-12 h-0.5 bg-gray-600 mb-2" />
              <div className="w-20 h-0.5 bg-gray-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      {currentScene === 0 && (
        <div className="fixed bottom-20 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="text-white/80 text-sm">Scroll down or click a scene</div>
          <svg className="w-6 h-6 mx-auto mt-2 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        </div>
      )}
    </div>
  );
};

export default CarParallaxAnimation;